import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tag, Tooltip, Spin, Form, Input, Select, Table, Button, InputNumber, Divider, DatePicker, Col, Row, Typography, Image, message, Popover, Popconfirm, Drawer, Empty, Modal, Checkbox } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FilePdfOutlined,
  FileWordOutlined,
  FileOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  FilePdfFilled,
  FileImageOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  FileTextOutlined,
  BulbFilled,
  DeleteOutlined,
  ReloadOutlined,
  LoadingOutlined,
  HistoryOutlined,
  EditFilled,
  PlusOutlined
} from '@ant-design/icons';
import './index.css';
// import moment from 'moment';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import apiClient from '../../utils/apiService';
import { DISPLAY_DATE_FORMAT, formatDisplayDate } from '../../utils/dateDisplay';
import toCamelCase from '../../utils/tocamelcase'; // 引入下划线转驼峰的函数
import { attachmentApi, workorderApi } from '../../api/workorder';

// 提取的子组件
import EmailDrawer from '../../components/emaildrawer';
import AttachmentDrawer from '../../components/attachmentdrawer';
import ANDrawer from '../../components/andrawer';
import FieldHistoryModal from '../../components/fieldhistorymodal';
import FieldIndexAutocomplete from '../../components/fieldindexautocomplete';
import AnAttachmentsPanel from './components/AnAttachmentsPanel';
import FilesUploadPanel from './components/FilesUploadPanel';
import SaveConfirmButton from './components/SaveConfirmButton';



// Customer Note 示例数据
const CUSTOMER_NOTE_EXAMPLES = [
  "OB/L REQUIRED.",
  "HB/L TELEX RLSD.",
  "RELEASE ORDER REQUIRED."
];

// Cost Information 中需要标红 label 的字段
const RED_COST_FIELD_NAMES = new Set([
  'other_cc',
  'doc_cc',
  'mbl_cc',
  'handling_fee',
  'profit_share',
  'total_cc',
  'final_balance',
  'balance_to_origin',
]);

const EMPHASIZED_COST_FIELD_NAMES = new Set([
  'final_balance',
]);

const CURRENCY_FIELD_NAMES = [
  'hbl_freight_ppd', 'of_cc', 'ddc_devanning', 'baf_cc', 'arb_cfs',
  'psc_pierpass', 'orc_port_security', 'gri_ctf', 'pss_exam_fee', 'acc_thc',
  'it_fee', 'doc_cc', 'ams_isf_fee', 'overdue_amount', 'other_cc', 'total_cc',
  'mbl_cc', 'handling_fee', 'trucking_fee', 'profit_share', 'balance_to_origin',
  'balance_change_amount1', 'balance_change_amount2', 'balance_change_amount3',
  'final_balance',
];

const CONTAINER_DATE_FIELDS = new Set([
  'pickup_date',
  'return_date',
  'last_free_day',
]);

const DEFAULT_FIELD_HEIGHT = 40;
const TEXTAREA_FIELD_HEIGHT = 80;

const getFieldHeight = (field = {}) => {
  if (field.height) return Number(field.height);
  if (field.type === 'textarea' || field.type === 'email-list') return TEXTAREA_FIELD_HEIGHT;
  return DEFAULT_FIELD_HEIGHT;
};

const getFieldWidth = (field = {}) => Math.max(0, Number(field.width) || 0);

const renderCostFieldLabel = (field) =>
  RED_COST_FIELD_NAMES.has(field?.name) ? (
    <span style={{
      color: '#c21617',
      fontWeight: EMPHASIZED_COST_FIELD_NAMES.has(field?.name) ? 800 : 700,
    }}
    >
      {field.label}
    </span>
  ) : (
    <span style={{ fontWeight: EMPHASIZED_COST_FIELD_NAMES.has(field?.name) ? 800 : 700 }}>
      {field.label}
    </span>
  );

const WorkOrderDetail = () => {

  const [messageApi, contextHolder] = message.useMessage();
  const { hblNo } = useParams();

  // 使用 navigate 进行页面跳转
  const navigate = useNavigate();

  // 表单实例
  const [form] = Form.useForm();
  // 使用 useWatch 监听 release_date 字段变化，立即触发重新渲染
  const releaseDateValue = Form.useWatch('release_date', form);

  const isReleaseDatePastMonth = useMemo(() => {
    if (!releaseDateValue) return false;
    const d = dayjs.isDayjs(releaseDateValue) ? releaseDateValue : dayjs(releaseDateValue, 'MM/DD/YYYY');
    return d.isValid() && d.isBefore(dayjs().startOf('month'));
  }, [releaseDateValue]);



  const [isEdit, setIsEdit] = useState(false);
  const [fieldDescriptions, setFieldDescriptions] = useState([]); // 存储字段说明信息
  const [isfieldDescShow, setIsfieldDescShow] = useState(false); // 控制加载状态
  const [containerFieldDescriptions, setContainerFieldDescriptions] = useState([]); // 存储容器字段说明信息
  const [isContainerFieldDescShow, setIsContainerFieldDescShow] = useState(false); // 控制容器字段modal显示
  const [autocompleteLoadingField, setAutocompleteLoadingField] = useState(null); // 自动补全加载中的字段名

  const handleAutocompleteFieldLoading = useCallback((fieldName, isLoading) => {
    setAutocompleteLoadingField(isLoading ? fieldName : null);
  }, []);



  const [containerData, setContainerData] = useState([]);
  const containerDraftRef = useRef([]);
  const [containerTotalsLive, setContainerTotalsLive] = useState({ teu: 0, packages: 0, gross_weight: 0, measurement: 0 });
  const totalsRafIdRef = useRef(null);
  const totalsPendingRef = useRef(containerTotalsLive);

  const calculateTotalsFromList = useCallback((list = []) => {
    return list.reduce((totals, item) => {
      return {
        teu: totals.teu + (parseFloat(item?.teu) || 0),
        packages: totals.packages + (parseFloat(item?.packages) || 0),
        gross_weight: totals.gross_weight + (parseFloat(item?.gross_weight) || 0),
        measurement: totals.measurement + (parseFloat(item?.measurement) || 0)
      };
    }, { teu: 0, packages: 0, gross_weight: 0, measurement: 0 });
  }, []);

  const scheduleContainerTotalsUpdate = useCallback((nextTotals) => {
    totalsPendingRef.current = nextTotals;
    if (totalsRafIdRef.current != null) return;
    totalsRafIdRef.current = requestAnimationFrame(() => {
      totalsRafIdRef.current = null;
      setContainerTotalsLive(totalsPendingRef.current);
    });
  }, []);

  useEffect(() => {
    if (isEdit) {
      containerDraftRef.current = (containerData || []).map((item) => ({ ...item }));
    }
  }, [isEdit, containerData]);

  useEffect(() => {
    scheduleContainerTotalsUpdate(calculateTotalsFromList(containerData || []));
  }, [containerData, calculateTotalsFromList, scheduleContainerTotalsUpdate]);

  useEffect(() => {
    return () => {
      if (totalsRafIdRef.current != null) {
        cancelAnimationFrame(totalsRafIdRef.current);
      }
    };
  }, []);
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const [workorderDetailData, setWorkorderDetailData] = useState(null);
  const [releaseValidationData, setReleaseValidationData] = useState(null);
  const releaseValidationRequestKeyRef = useRef('');

  // OP list
  const [opList, setOpList] = useState([]);

  // 附件相关状态
  const [showAttachmentPanel, setShowAttachmentPanel] = useState(false);
  const [attachments, setAttachments] = useState([]); // 邮件附件
  const [selectedArchiveAttachmentKeys, setSelectedArchiveAttachmentKeys] = useState([]);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const [activeAttachmentTab, setActiveAttachmentTab] = useState('attachments');
  const [uploadedFiles, setUploadedFiles] = useState([]); // 上传的文件 (display state, synced from ref)
  const uploadedFilesRef = useRef([]); // actual source of truth — updates don't trigger re-render
  const [uploadedFilesLoading, setUploadedFilesLoading] = useState(false);
  const [uploadedFilesError, setUploadedFilesError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [archiveEmailAttachmentsLoading, setArchiveEmailAttachmentsLoading] = useState(false);


  // AN/PU 附件相关状态
  const [anAttachments, setAnAttachments] = useState([]); // display state, synced from ref
  const anAttachmentsRef = useRef([]); // actual source of truth
  const [anAttachmentsLoading, setAnAttachmentsLoading] = useState(false);
  const [anAttachmentsError, setAnAttachmentsError] = useState(null);
  const [anUploading, setAnUploading] = useState(false);
  const [selectedAnAttachment, setSelectedAnAttachment] = useState(null);
  const [anEmailType, setAnEmailType] = useState(0); // 0 = AN, 1 = PU



  // 备注编辑状态
  const [editingRemarkId, setEditingRemarkId] = useState(null);
  const [editingRemarkType, setEditingRemarkType] = useState(''); // 'file' | 'an'、
  const [savingRemark, setSavingRemark] = useState(false);


  // 用户信息
  const [userInfo, setUserInfo] = useState('');

  // 辅助函数：安全创建 dayjs 对象，避免 isValid 错误
  const safeDayjs = useCallback((value, format) => {
    if (!value) return null;
    try {
      const parsed = format ? dayjs(value, format, true) : dayjs(value);
      // 检查是否是有效的 dayjs 对象
      if (dayjs.isDayjs(parsed) && parsed.isValid && parsed.isValid()) {
        return parsed;
      }
      return null;
    } catch (e) {
      console.warn('dayjs parsing error:', e);
      return null;
    }
  }, []);




  // 获取用户info
  useEffect(() => {
    // 获取用户info
    try {
      const userinfo = JSON.parse(localStorage.getItem('userInfo'));
      if (userinfo) {
        setUserInfo(userinfo);
      }
    } catch (error) {
      console.log('Error getting user info:', error);
    }



    // 获取op list数据
    const fetchOpListData = async () => {
      try {
        const response = await apiClient.get(`/prime/auth/op-info-list`);

        // 获取 op List
        setOpList(response.data.data);
      } catch (error) {
        console.error('Error fetching Op List data:', error);
      }
    };

    fetchOpListData();



  }, []);



  // 扩展 dayjs 以支持自定义解析格式
  dayjs.extend(customParseFormat);



  const normalizeDateForValidation = useCallback((value) => {
    if (!value) return '';

    if (dayjs.isDayjs(value)) {
      return value.isValid() ? value.format('YYYY-MM-DD') : '';
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) return '';

      const strictParsed = dayjs(trimmedValue, [
        'YYYY-MM-DD',
        'MM/DD/YYYY',
        'MM-DD-YYYY',
        'YYYY/MM/DD',
      ], true);

      if (strictParsed.isValid()) {
        return strictParsed.format('YYYY-MM-DD');
      }

      const fallbackParsed = dayjs(trimmedValue);
      return fallbackParsed.isValid() ? fallbackParsed.format('YYYY-MM-DD') : '';
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }, []);

  // 处理时间是字符串的问题
  const processFormData = (data) => {
    const processedData = { ...data };

    // 定义所有需要转换的日期字段
    const dateFields = [
      'etd', 'eta_port', 'customs_entry_date', 'customs_exam_date',
      'customs_release_date', 'go_date', 'pickup_issuing_date',
      'eta_port_update', 'dest_eta', 'an_date', 'it_date',
      'confirmation_date', 'advice_date', 'hbl_receipt_date',
      'payment_receipt_date', 'release_date'  // 包含 release_date
    ];

    dateFields.forEach(field => {
      if (processedData[field]) {
        // 将字符串转换为 dayjs 对象
        processedData[field] = dayjs(processedData[field], 'MM/DD/YYYY');
      }
    });

    CURRENCY_FIELD_NAMES.forEach((field) => {
      if (processedData[field] == null || processedData[field] === '') {
        processedData[field] = 0;
      }
    });

    return processedData;
  };

  const validateReleaseDateOnMount = useCallback(async (detailData) => {
    const targetHblNo = String(
      detailData?.hbl_no
      || detailData?.hblNo
      || form.getFieldValue('hbl_no')
      || hblNo
      || ''
    ).trim();
    const releaseDate = normalizeDateForValidation(
      detailData?.release_date
      || detailData?.releaseDate
      || form.getFieldValue('release_date')
    );
    const agencyValue = detailData?.agency ?? form.getFieldValue('agency') ?? '';
    const agency = typeof agencyValue === 'string' ? agencyValue.trim() : '';

    if (!targetHblNo) {
      return;
    }

    const requestKey = `${targetHblNo}|${releaseDate}|${agency}`;
    if (releaseValidationRequestKeyRef.current === requestKey) {
      return;
    }
    releaseValidationRequestKeyRef.current = requestKey;

    try {
      const response = await apiClient.post('/shipping-order/release/validate', {
        hbl_no: targetHblNo,
        release_date: releaseDate || '',
        ...(agency ? { agency } : {}),
      });

      const responseCode = response?.data?.code;
      const responseMessage = response?.data?.message;

      if (responseCode === 200) {
        messageApi.success(responseMessage || 'Release validation passed');
        return;
      }

      messageApi.warning(responseMessage || 'Release validation did not pass');
    } catch (error) {
      releaseValidationRequestKeyRef.current = '';
      messageApi.error(error?.response?.data?.message || error?.message || 'Release validation failed');
    }
  }, [form, hblNo, messageApi, normalizeDateForValidation]);



  // 根据 HBL 单号获取详情数据
  const fetchDetailData = async () => {
    try {
      const response = await apiClient.get(`/shipping-order/detail/${encodeURIComponent(hblNo)}`);
      const rawDetailData = response?.data?.data || {};
      const infoData = processFormData(rawDetailData);
      // 设置整个工作订单详情数据
      setWorkorderDetailData(infoData);
      setReleaseValidationData(rawDetailData);


      // 根据 source_flag 和用户角色设置 isEdit 初始状态
      const isAdmin = userInfo && userInfo.username === 'admin';
      const isPms = infoData.source_flag === 'pms';

      if (isAdmin) {
        // 管理员始终可编辑
        setIsEdit(true);
      } else if (isPms) {
        // 非管理员且来源为 pms，不可编辑
        setIsEdit(false);
      } else {
        // 非管理员且来源非 pms，可编辑
        setIsEdit(true);
      }


      // 简化日期解析函数
      const parseToDayjs = (val, format) => {
        // 确保val不是null或undefined
        if (val == null || val === '') return null;

        // 如果已经是有效的dayjs对象，直接返回
        if (dayjs.isDayjs(val)) {
          return val;
        }

        // 尝试解析字符串或数字
        if (typeof val === 'string' || typeof val === 'number') {
          const parsed = format ? dayjs(val, format, true) : dayjs(val);
          if (dayjs.isDayjs(parsed)) {
            return parsed;
          }
        }

        return null;
      };

      // 从 formConfig 中收集所有日期字段及其格式
      const collectDateFields = (cfg) => {
        const map = new Map();

        const addField = (field) => {
          if (field && field.type === 'date' && field.name) {
            map.set(field.name, field.format || 'MM/DD/YYYY');
          }
        };

        // 顶层数组分组
        [
          'workorderInfo',
          'entrustmentInfo',
          'shipperInfo',
          'consigneeInfo',
          'notifyPartyInfo',
          'shipmentUpdateInfo',
          'otherInfo'
        ].forEach(key => {
          (cfg[key] || []).forEach(addField);
        });

        return map;
      };

      const dateFieldMap = collectDateFields(formConfig);

      // 根据后端返回生成表单初始值
      const formattedData = Object.keys(infoData).reduce((acc, key) => {
        const value = infoData[key];

        if (dateFieldMap.has(key)) {
          const fmt = dateFieldMap.get(key);
          acc[key] = parseToDayjs(value, fmt);
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});

      // 为每个容器分配唯一ID
      const containersWithIds = (infoData.containers || []).map((container, index) => ({
        ...container,
        id: container.id || `container_${index}_${Date.now()}` // 如果没有ID则生成唯一ID
      }));

      setContainerData(containersWithIds || []);
      containerDraftRef.current = containersWithIds || [];

      form.setFieldsValue(formattedData);

    } catch (error) {
      console.error('Error fetching detail data:', error);
    }
  };
  // 设置表单初始值
  useEffect(() => {
    fetchDetailData();

    // 设置动态tab 标题
    if (hblNo) {
      document.title = hblNo;
    }
    return () => {
      document.title = 'PAi Pro';
    };

  }, [hblNo]);

  useEffect(() => {
    if (!releaseValidationData) return;
    validateReleaseDateOnMount(releaseValidationData);
  }, [releaseValidationData, validateReleaseDateOnMount]);

  // 数据加载完成后计算费用
  useEffect(() => {
    // 延迟执行以确保表单值已设置
    const timer = setTimeout(() => {
      handleFeeChange();
    }, 100);

    return () => clearTimeout(timer);
  }, [containerData]); // 当容器数据加载完成时触发


  const formConfig = useMemo(() => ({
    "workorderInfo": [
      { "label": "ID", "name": "pms_id", "type": "label", "x": 0, "y": 0, "width": 180 },
      { "label": "Agency", "name": "agency", "type": "label", "x": 188, "y": 0, "width": 180 },
      { "label": "Created By", "name": "created_by", "type": "label", "x": 376, "y": 0, "width": 180 },
      { "label": "Created Time", "name": "created_at", "type": "label", "x": 564, "y": 0, "width": 180 },
      { "label": "Black Status", "name": "blacklist_flag", "type": "label", "x": 752, "y": 0, "width": 180 },
      { "label": "PIC", "name": "pic", "type": "select", "options": opList || [], "x": 0, "y": 40, "width": 180 },

      // { "label": "Nominated", "name": "nominated", "type": "label" },// 放到最下面了
      // { "label": "Salesman", "name": "salesman", "type": "label" },  // 放到最下面了

    ],
    "entrustmentInfo": [
      { "label": "HB/L No.", "name": "hbl_no", "type": "text", "x": 0, "y": 0, "width": 180 },
      { "label": "MB/L No.", "name": "mbl_no", "type": "text", "x": 188, "y": 0, "width": 180 },
      { "label": "BKG No.", "name": "booking_no", "type": "text", "x": 376, "y": 0, "width": 180 },
      { "label": "AMS B/L", "name": "ams_bl", "type": "text", "x": 564, "y": 0, "width": 180 },
      {
        "label": "Carrier", "name": "carrier", "type": "select", "x": -50, "y": 0, "width": 240, "options": [
          { "value": "APL", "label": "APL" },
          { "value": "CMA-CGM", "label": "CMA-CGM" },
          { "value": "COSCO", "label": "COSCO" },
          { "value": "CULINES", "label": "CULINES" },
          { "value": "EVERGREEN", "label": "EVERGREEN" },
          { "value": "HAMBURG-SUD", "label": "HAMBURG-SUD" },
          { "value": "HAPAG-LIOYD", "label": "HAPAG-LIOYD" },
          { "value": "HMM", "label": "HMM" },
          { "value": "MAERSK", "label": "MAERSK" },
          { "value": "MATSON", "label": "MATSON" },
          { "value": "MSC", "label": "MSC" },
          { "value": "ONE", "label": "ONE" },
          { "value": "OOCL", "label": "OOCL" },
          { "value": "PIL", "label": "PIL" },
          { "value": "SM LINES", "label": "SM LINES" },
          { "value": "WAN HAI", "label": "WAN HAI" },
          { "value": "YANG MING", "label": "YANG MING" },
          { "value": "ZIM", "label": "ZIM" }
        ] || []
      },
      // { "label": "Carrier", "name": "carrier", "type": "select", "options": ["Select Carrier", "MAERSK", "COSCO", "ONE"] },
      { "label": "Vessel", "name": "vessel_name", "type": "text", "x": 110, "y": 0, "width": 300 },
      { "label": "VOY", "name": "voyage", "type": "text", "x": 320, "y": 0, "width": 300 },

      { "label": "ETD", "name": "etd", "type": "date", "format": "MM/DD/YYYY", "x": 0, "y": 35, "width": 250 },
      { "label": "ETA Port", "name": "eta_port", "type": "date", "format": "MM/DD/YYYY", "x": 300, "y": 35, "width": 250 },

      { "label": "Place of Receipt", "name": "place_of_receipt", "type": "text", "x": 0, "y": 70, "width": 528 },
      { "label": "Port of Loading", "name": "port_of_loading", "type": "text", "x": 0, "y": 105, "width": 528 },
      { "label": "Port of Discharge", "name": "port_of_discharge", "type": "text", "x": 0, "y": 140, "width": 528 },
      { "label": "Place of delivery", "name": "place_of_delivery", "type": "text", "x": 0, "y": 175, "width": 528 }
    ],
    "shipperInfo": [
      { "label": "Company Name", "name": "shipper_company", "type": "text", "required": true, "x": 0, "y": 0, "width": 600 },
      { "label": "Address Line 1", "name": "shipper_address1", "type": "text", "required": true, "x": 0, "y": 35, "width": 600 },
      { "label": "Address Line 2", "name": "shipper_address2", "type": "text", "x": 0, "y": 70, "width": 600 },
      { "label": "Tel No.", "name": "shipper_tel", "type": "text", "x": 0, "y": 105, "width": 300 },
      { "label": "Contact Person", "name": "shipper_contact", "type": "text", "x": 300, "y": 105, "width": 300 },
      { "label": "E-Mail", "name": "shipper_email", "type": "email-list", "x": 0, "y": 140, "width": 600 },
    ],
    "consigneeInfo": [
      { "label": "Company Name", "name": "consignee_company", "type": "text", "required": true, "x": 0, "y": 0, "width": 600 },
      { "label": "Address Line 1", "name": "consignee_address1", "type": "text", "required": true, "x": 0, "y": 35, "width": 600 },
      { "label": "Address Line 2", "name": "consignee_address2", "type": "text", "x": 0, "y": 70, "width": 600 },
      { "label": "Tel No.", "name": "consignee_tel", "type": "text", "x": 0, "y": 105, "width": 300 },
      { "label": "Contact Person", "name": "consignee_contact", "type": "text", "x": 300, "y": 105, "width": 300 },
      { "label": "E-Mail", "name": "consignee_email", "type": "email-list", "x": 0, "y": 140, "width": 600 }
    ],
    "notifyPartyInfo": [
      { "label": "Company Name", "name": "notify_company", "type": "text", "required": true, "x": 0, "y": 0, "width": 600 },
      { "label": "Address Line 1", "name": "notify_address1", "type": "text", "required": true, "x": 0, "y": 35, "width": 600 },
      { "label": "Address Line 2", "name": "notify_address2", "type": "text", "x": 0, "y": 70, "width": 600 },
      { "label": "Tel No.", "name": "notify_tel", "type": "text", "x": 0, "y": 105, "width": 300 },
      { "label": "Contact Person", "name": "notify_contact", "type": "text", "x": 300, "y": 105, "width": 300 },
      { "label": "E-Mail", "name": "notify_email", "type": "email-list", "x": 0, "y": 140, "width": 600 },
    ],

    "container_cargo": [
      { "label": "Customs Entry Date", "name": "customs_entry_date", "type": "date", "format": "MM/DD/YYYY", "x": 0, "y": 0, "width": 300 },
      { "label": "Customs Exam Date", "name": "customs_exam_date", "type": "date", "format": "MM/DD/YYYY", "x": 310, "y": 0, "width": 300 },
      { "label": "Customs Release Date", "name": "customs_release_date", "type": "date", "format": "MM/DD/YYYY", "x": 620, "y": 0, "width": 300 },
      { "label": "G.O. Date", "name": "go_date", "type": "date", "format": "MM/DD/YYYY", "x": 930, "y": 0, "width": 300 },

    ],

    "containerInfo": [
      { title: "Container No.", dataIndex: "container_no", key: "container_no", width: 100 },
      { title: "Seal No.", dataIndex: "seal_no", key: "seal_no", width: 120 },
      { title: "Return Date", dataIndex: "return_date", key: "return_date", width: 200, onHeaderCell: () => ({ style: { color: '#c21617' } }) },
      { title: "Pickup Date", dataIndex: "pickup_date", key: "pickup_date", width: 200, onHeaderCell: () => ({ style: { color: '#c21617' } }) },
      { title: "TEU", dataIndex: "teu", key: "teu", width: 80, },
      { title: "No of Pkgs", dataIndex: "packages", key: "packages", width: 100 },
      { title: "Description of Goods", dataIndex: "goods_description", key: "goods_description", width: 60 },
      { title: "Gross Weight (kg)", dataIndex: "gross_weight", key: "gross_weight", width: 60 },
      { title: "Measurement (cbm)", dataIndex: "measurement", key: "measurement", width: 60 },
      { title: "Pickup Number", dataIndex: "pickup_number", key: "pickup_number", width: 160, onHeaderCell: () => ({ style: { color: '#c21617' } }) },
      { title: "Last Free Day", dataIndex: "last_free_day", key: "last_free_day", width: 200, onHeaderCell: () => ({ style: { color: '#c21617' } }) },
      { title: "DD Note", dataIndex: "dd_note", key: "dd_note", width: 160 },
    ],
    "shipmentUpdateInfo": [
      { "label": "Advice Date", "name": "advice_date", "type": "date", "format": "MM/DD/YYYY", "x": 0, "y": 0, "width": 300 },
      { "label": "ETA Port", "name": "eta_port_update", "type": "date", "format": "MM/DD/YYYY", "x": 310, "y": 0, "width": 300, "labelColor": "#c21617" },
      { "label": "Dest. ETA", "name": "dest_eta", "type": "date", "format": "MM/DD/YYYY", "x": 620, "y": 0, "width": 300 },
      { "label": "AN Date", "name": "an_date", "type": "date", "format": "MM/DD/YYYY", "x": 930, "y": 0, "width": 300 },

      { "label": "I.T.#", "name": "it_number", "type": "text", "x": 200, "y": 35, "width": 300 },
      { "label": "I.T Place", "name": "it_place", "type": "text", "x": 510, "y": 35, "width": 300 },
      { "label": "I.T. Date", "name": "it_date", "type": "date", "format": "MM/DD/YYYY", "x": 820, "y": 35, "width": 300 },

      { "label": "Freight Location", "name": "freight_location", "type": "text", "x": 0, "y": 70, "width": 600 },
      { "label": "Freight Tel", "name": "freight_tel", "type": "text", "x": 620, "y": 70, "width": 300 },


      { "label": "Empty Return", "name": "empty_return", "type": "textarea", "x": 0, "y": 105, "width": 600 },
      { "label": "Chassis Return", "name": "chassis_return", "type": "textarea",  "x": 620, "y": 105, "width": 600 },
      // { "label": "BGN Special OPTN Date", "name": "bgn_special_optn_date", "type": "text" },

      // { "label": "NewShipProcess Confirmed By", "name": "confirmed_by", "type": "text" },
      // { "label": "Confirmation Date", "name": "confirmation_date", "type": "date", "format": "MM/DD/YYYY" },


      // 从releaseInfo 挪上来
      { "label": "P/U # Issuing Date", "name": "pickup_issuing_date", "type": "date", "format": "MM/DD/YYYY", "labelColor": "#c21617", "x": 0, "y": 210, "width": 300 },



    ],
    "releaseInfo": [
      { "label": "HBL Receipt Date", "name": "hbl_receipt_date", "type": "date", "format": "MM/DD/YYYY", "x": 0, "y": 0, "width": 280 },
      { "label": "Payment Receipt Date", "name": "payment_receipt_date", "type": "date", "format": "MM/DD/YYYY", "x": 260, "y": 0, "width": 280 },
      { "label": "Release Date", "name": "release_date", "type": "date", "format": "MM/DD/YYYY", "x": 520, "y": 0, "width": 280 },
      {
        "label": "Release Time", "name": "release_flag", "type": "select", "x": 780, "y": 0, "width": 190,
        "options": [{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }]
      }, // release -> release_flag
      {
        "label": "Cashier", "name": "cashier_type", "type": "select", "x": 960, "y": 0, "width": 300,
        "options": [
          { value: 'CS0001', label: 'CS0001' },
          { value: 'CS0002', label: 'CS0002' },
          { value: 'CS0003', label: 'CS0003' },
          { value: 'CS0004', label: 'CS0004' },
          { value: 'CS0005', label: 'CS0005' },
          { value: 'CS0006', label: 'CS0006' },
          { value: 'CS0007', label: 'CS0007' },
          { value: 'CS0008', label: 'CS0008' },
          { value: 'CS0009', label: 'CS0009' }
        ]
      },
    ],




    "costInfo": {
      "collectForAgent": [
        { "label": "HB/L Freight PPD", "name": "hbl_freight_ppd", "type": "currency", "x": 0, "y": 0, "width": 150 },
        { "label": "O/F CC", "name": "of_cc", "type": "currency", "x": 158, "y": 0, "width": 150 },
        { "label": "DDC Devanning", "name": "ddc_devanning", "type": "currency", "x": 316, "y": 0, "width": 150 },
        { "label": "BAF CC", "name": "baf_cc", "type": "currency", "x": 474, "y": 0, "width": 150 },
        { "label": "ARB CFS", "name": "arb_cfs", "type": "currency", "x": 0, "y": 30, "width": 150 },
        { "label": "PSC Pierpass", "name": "psc_pierpass", "type": "currency", "x": 158, "y": 35, "width": 150 },
        { "label": "ORC Port Security", "name": "orc_port_security", "type": "currency", "x": 316, "y": 35, "width": 150 },
        { "label": "GRI CTF", "name": "gri_ctf", "type": "currency", "x": 474, "y": 35, "width": 150 },
        { "label": "PSS Exam Fee", "name": "pss_exam_fee", "type": "currency", "x": 0, "y": 70, "width": 150 },
        { "label": "ACC THC", "name": "acc_thc", "type": "currency", "x": 158, "y": 70, "width": 150 },
        { "label": "I.T Fee", "name": "it_fee", "type": "currency", "x": 316, "y": 70, "width": 150 },
        { "label": "DOC CC", "name": "doc_cc", "type": "currency", "x": 474, "y": 70, "width": 150 },
        { "label": "AMS ISF FEE", "name": "ams_isf_fee", "type": "currency", "x": 0, "y": 105, "width": 150 },
        { "label": "Overdue Amount", "name": "overdue_amount", "type": "currency", "x": 158, "y": 105, "width": 150 },
        { "label": "Other CC", "name": "other_cc", "type": "currency", "x": 316, "y": 105, "width": 150 }, // other_cc
        { "label": "Total CC", "name": "total_cc", "type": "currency", "x": 474, "y": 105, "width": 150 },
      ],
      "payToPrime": [
        { "label": "MBL CC", "name": "mbl_cc", "type": "currency", "x": 0, "y": 0, "width": 220 },
        { "label": "Handling Fee", "name": "handling_fee", "type": "currency", "x": 0, "y": 40, "width": 220 },
        { "label": "Trucking Fee", "name": "trucking_fee", "type": "currency", "x": 0, "y": 80, "width": 220 },
        { "label": "Profit Share", "name": "profit_share", "type": "currency", "x": 0, "y": 120, "width": 220 },
        { "label": "Balance To Origin", "name": "balance_to_origin", "type": "currency", "x": 0, "y": 160, "width": 220 },
      ],



      "balance_change": [
        { "label": "1", "name": "balance_change1", "type": "text", "x": 0, "y": 0, "width": 110 },
        { "label": "amount1", "name": "balance_change_amount1", "type": "currency", "x": 126, "y": 0, "width": 110 },
        { "label": "2", "name": "balance_change2", "type": "text", "x": 0, "y": 40, "width": 110 },
        { "label": "amount2", "name": "balance_change_amount2", "type": "currency", "x": 126, "y": 40, "width": 110 },
        { "label": "3", "name": "balance_change3", "type": "text", "x": 0, "y": 80, "width": 110 },
        { "label": "amount3", "name": "balance_change_amount3", "type": "currency", "x": 126, "y": 80, "width": 110 },
      ],
      "costbottom": [
        // final_balance
        { "label": "Final Balance", "name": "final_balance", "type": "currency", "x": 0, "y": 0, "width": 236 },
      ],
      "note": [
        { "label": "Origin Note", "name": "origin_note", "type": "textarea", "x": 0, "y": 0, "width": 400 },
        { "label": "​Hold Note", "name": "hold_note", "type": "textarea", "x": 500, "y": 0, "width": 200 },
      ]
    },
    "otherInfo": [
      { "label": "Prime Note", "name": "prime_note", "type": "textarea", "x": 0, "y": 0, "width": 500 },
      { "label": "Customer Note", "name": "customer_note", "type": "textarea", "x": 0, "y": 105, "width": 500 },
      
      { "label": "AN Remark", "name": "an_remark", "type": "text", "x": 0, "y": 210, "width": 500 },
      { "label": "PU Remark", "name": "pu_remark", "type": "text", "x": 0, "y": 245, "width": 500 },

      { "label": "Nominated", "name": "nominated", "type": "text", "x": 0, "y": 280, "width": 240},
      { "label": "Salesman", "name": "salesman", "type": "text", "x": 250, "y": 280, "width": 240},

    ]


  }), [opList]);


  // 自定义表单区块组件
  const renderPositionedFields = (fields = []) => {
    const positionedFields = fields
      .filter((field) => field && field.label)
      .slice();

    const containerHeight = positionedFields.reduce((max, field) => {
      const fieldBottom = (Number(field.y) || 0) + getFieldHeight(field);
      return Math.max(max, fieldBottom);
    }, 0);

    return (
      <div className="absolute-section-layout" style={{ minHeight: containerHeight }}>
        {positionedFields.map((field) => {
          const fieldWidth = getFieldWidth(field);
          const fieldHeight = getFieldHeight(field);

          return (
            <div
              key={field.name}
              className="absolute-section-field"
              style={{
                left: `${Number(field.x) || 0}px`,
                top: `${Number(field.y) || 0}px`,
                width: `${fieldWidth}px`,
                minHeight: `${fieldHeight}px`,
              }}
            >
              <FormField field={field} />
            </div>
          );
        })}
      </div>
    );
  };

  const FormSection = ({ title, children, columns = 3, className = '', extra = null, fields = null }) => (
    <div className={`form-section ${className}`.trim()}>
      {title != 'Container Cargo' && <div className="section-header" style={extra ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } : {}}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="header-line" />
          <h3>{title}</h3>
        </div>
        {extra}
      </div>}
      {fields ? renderPositionedFields(fields) : (
        <div className={className === 'workorderinfo' ? `${className}` : `form-grid columns-${columns}`}>
          {children}
        </div>
      )}
    </div>
  );

  const renderPartyInfoSection = (fields = []) => {
    return renderPositionedFields(fields);
  };

  const renderTransportInfoSection = (fields = []) => {
    return renderPositionedFields(fields);
  };

  const formatCostNumberInput = useCallback((value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'string' && value.endsWith('.')) return value;

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value;

    return Number(numericValue.toFixed(2)).toString();
  }, []);

  const parseCostNumberInput = useCallback((value) => {
    if (value == null) return '';
    return String(value).replace(/,/g, '').trim();
  }, []);


  // 自定义表单字段组件
  const FormField = ({ field = {} }) => {



    const renderInput = () => {

      switch (field.type) {
        case 'label':
          // 特殊处理 blacklist_flag 字段
          if (field.name === 'blacklist_flag') {
            const blacklistFlag = form.getFieldValue(field.name);
            return (
              <Typography.Text
                className='typographytext'
                style={{
                  color: blacklistFlag === 1 ? '#ff4d4f' : '#52c41a',
                  fontWeight: 'bold'
                }}
              >
                {blacklistFlag === 1 ? 'In Black List' : 'OK'}
              </Typography.Text>
            );
          }
          return (
            <Typography.Text className='typographytext'>
              {form.getFieldValue(field.name) || 'N/A'} {/* 动态获取值 */}
            </Typography.Text>
          );
        case 'select':
          return (
            <Select
              value={form.getFieldValue(field.name)}
              disabled={!isEdit || (field.name === 'release_flag' && userInfo.canEditFinance === 0) || (field.name === 'cashier_type' && releaseDateValue && userInfo?.userRole !== 'Admin')}
              showSearch
              allowClear
              style={{ width: '100%' }}
            >
              {field.options?.map((opt, index) => (
                <Select.Option
                  key={index}
                  value={typeof opt === 'object' && opt !== null && !Array.isArray(opt) && Object.keys(opt).length === 0 ? '' : (Object(opt) ? opt.value : opt)}
                >
                  {typeof opt === 'object' && opt !== null && !Array.isArray(opt) && Object.keys(opt).length === 0 ? '' : (Object(opt) ? opt.label : opt)}
                </Select.Option>
              ))}
            </Select>
          );
        case 'date':
          return (
            <DatePicker
              style={{ width: '100%' }}
              format={DISPLAY_DATE_FORMAT}
              disabled={!isEdit || (['payment_receipt_date', 'hbl_receipt_date', 'release_date'].includes(field.name) && userInfo.canEditFinance === 0) || (field.name === 'release_date' && isReleaseDatePastMonth)}
              valueFormat="MM/DD/YYYY"
            />
          )
        case 'textarea':
          return (
            <Input.TextArea
              rows={4}
              disabled={!isEdit || ['origin_note', 'hold_note'].includes(field.name)}
            />);
        case 'currency':
          return (
            <InputNumber
              style={{ width: '100%' }}
              step="0.01"
              value={form.getFieldValue(field.name) ?? 0}
              onChange={(value) => handleCurrencyChange(field.name, value)}
              disabled={(field.name === 'total_cc' || field.name === 'balance_to_origin') || !isEdit}
              controls={false}
              prefix="$"
            />);
        case 'text':
          return (
            <Input
              disabled={field.name === 'hbl_no' ? true : !isEdit}
            />

          );
        case 'email-list':
          // 处理邮箱列表，支持多行显示
          if (!isEdit) {
            const emailValue = form.getFieldValue(field.name);
            if (!emailValue) return <Typography.Text>N/A</Typography.Text>;

            // 按分号、逗号或换行符分割邮箱
            const emails = emailValue.split(/[;,\n]/).map(e => e.trim()).filter(e => e);

            // 如果只有一个邮箱且没有分隔符，直接显示
            if (emails.length === 1 && emails[0] === emailValue.trim()) {
              return (
                <Typography.Text style={{ whiteSpace: 'pre-line', wordBreak: 'break-all' }}>
                  {emailValue}
                </Typography.Text>
              );
            }

            // 多个邮箱，每行显示一个
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {emails.map((email, index) => (
                  <Typography.Text key={index} style={{ display: 'block' }}>
                    {email}
                  </Typography.Text>
                ))}
              </div>
            );
          }
          return (
            <Input.TextArea
              rows={3}
              disabled={!isEdit}
              placeholder="Enter emails separated by semicolon, comma or new line"
            />
          );
        default:
          return (
            <Input disabled={!isEdit} />
          );
      }
    };



    return (

      <div
        className={`workorderinfo-item ${field.type === 'label' ? 'workorderinfo-label-item' : ''} ${field.type === 'select' && field.name === 'pic' ? 'workorderinfo-select-item' : ''}`}
      >
        {/* 渲染的字段 */}
        <Form.Item
          label={field.labelColor ? <span style={{ color: field.labelColor }}>{field.label || 'Unknown Label'}</span> : (field.label || 'Unknown Label')}
          name={field.name}
          initialValue={field.type === 'currency' ? 0 : undefined}
        >
          {renderInput()}
        </Form.Item>


        {/* 变化信息的icon */}
        {field.type !== 'label' &&
          <>
            {field.name === 'customer_note' && (
              <Popover
                content={
                  <div style={{ maxWidth: 300 }}>
                    {CUSTOMER_NOTE_EXAMPLES.map((example, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => {
                          const current = form.getFieldValue(field.name) || '';
                          const newValue = current ? `${current} ${example}` : example;
                          form.setFieldsValue({ [field.name]: newValue });
                        }}
                      >
                        {example}
                      </div>
                    ))}
                  </div>
                }
                title="Example"
                trigger="click"
                placement="bottomLeft"
              >
                <div
                  className='workorderinfo-item-icon'
                  style={{ marginRight: '20px' }}
                >
                  <BulbFilled style={{ color: '#ffb300' }} />
                </div>
              </Popover>
            )}

            {/* 变化信息的icon */}
            {/* <div
              className='workorderinfo-item-icon'
              onClick={() => fetchFieldDescription(field.name)}
            >
              {autocompleteLoadingField === field.name ? (
                <LoadingOutlined style={{ color: '#1f63fb' }} spin />
              ) : (
                <InfoCircleOutlined style={{ color: '#1f63fb' }} />
              )}
            </div> */}
          </>
        }
      </div>
    );
  };


  // 处理container table
  dayjs.extend(customParseFormat);


  // 处理容器数据变化
  const handleContainerChange = useCallback((index, field, value) => {
    const current = containerDraftRef.current || [];
    if (!current[index]) return;
    current[index] = { ...current[index], [field]: value };
    containerDraftRef.current = current;
    scheduleContainerTotalsUpdate(calculateTotalsFromList(current));
  }, [calculateTotalsFromList, scheduleContainerTotalsUpdate]);

  // 添加新容器行
  const handleAddContainer = useCallback(() => {
    const newContainer = {
      id: Date.now() + (Math.random() * 10000), // 临时ID
      container_no: '',
      seal_no: '',
      teu: '',
      packages: '',
      goods_description: '',
      gross_weight: '',
      measurement: '',
      pickup_date: '',
      return_date: '',
      pickup_number: '',
      last_free_day: '',
      dd_note: ''
    };
    setContainerData((prev) => {
      const next = [...prev, newContainer];
      containerDraftRef.current = next;
      scheduleContainerTotalsUpdate(calculateTotalsFromList(next));
      return next;
    });
  }, [calculateTotalsFromList, scheduleContainerTotalsUpdate]);

  // 删除容器行
  const handleDeleteContainer = useCallback((id) => {
    setContainerData((prev) => {
      const next = prev.filter((item) => item.id !== id);
      containerDraftRef.current = next;
      scheduleContainerTotalsUpdate(calculateTotalsFromList(next));
      return next;
    });
  }, [calculateTotalsFromList, scheduleContainerTotalsUpdate]);
  const containerTotals = containerTotalsLive;

  const containerTableRows = useMemo(() => {
    return containerData.map((item, index) => ({
      ...item,
      key: item.id || index,
      _rowIndex: index,
    }));
  }, [containerData]);

  const containerTotalRow = useMemo(() => {
    return {
      key: 'total',
      container_no: 'Total',
      seal_no: '',
      teu: containerTotals.teu,
      packages: containerTotals.packages,
      goods_description: '',
      gross_weight: containerTotals.gross_weight,
      measurement: containerTotals.measurement,
      pickup_date: '',
      return_date: '',
      pickup_number: '',
      last_free_day: ''
    };
  }, [containerTotals]);

  const containerTableDataSource = useMemo(() => {
    return [...containerTableRows, containerTotalRow];
  }, [containerTableRows, containerTotalRow]);

  const containerBottomFieldKeys = useMemo(() => ([
    'pickup_number',
    'last_free_day',
    'pickup_date',
    'return_date',
    'dd_note',
  ]), []);

  const containerBottomFields = useMemo(
    () => ['container_no', ...containerBottomFieldKeys]
      .map((fieldKey) => formConfig.containerInfo.find((field) => field.dataIndex === fieldKey))
      .filter(Boolean),
    [containerBottomFieldKeys, formConfig.containerInfo]
  );

  const renderContainerInput = useCallback((record, fieldName) => {
    const rowIndex = record._rowIndex;
    if (rowIndex < 0 || record.key === 'total') return record[fieldName];

    switch (fieldName) {
      case 'container_no':
      case 'seal_no':
      case 'goods_description':
      case 'pickup_number':
        return (
          <Input
            defaultValue={record[fieldName]}
            onChange={(e) => handleContainerChange(rowIndex, fieldName, e.target.value)}
            disabled={!isEdit}
          />
        );
      case 'teu':
        return (
          <InputNumber
            defaultValue={record.teu}
            onChange={(value) => handleContainerChange(rowIndex, 'teu', value)}
            controls={false}
            style={{ width: 40 }}
            disabled={!isEdit}
          />
        );
      case 'packages':
      case 'measurement':
        return (
          <InputNumber
            defaultValue={record[fieldName]}
            onChange={(value) => handleContainerChange(rowIndex, fieldName, value)}
            controls={false}
            disabled={!isEdit}
          />
        );
      case 'gross_weight':
        return (
          <InputNumber
            defaultValue={record.gross_weight}
            onChange={(value) => handleContainerChange(rowIndex, 'gross_weight', value)}
            step="0.01"
            controls={false}
            disabled={!isEdit}
          />
        );
      case 'pickup_date':
      case 'return_date':
      case 'last_free_day':
        return (
          <DatePicker
            key={`${record.id || rowIndex}-${fieldName}-${record[fieldName] || ''}`}
            defaultValue={safeDayjs(record[fieldName], 'MM/DD/YYYY') || safeDayjs(record[fieldName], 'YYYY-MM-DD')}
            onChange={(_, dateString) => handleContainerChange(rowIndex, fieldName, dateString)}
            format={DISPLAY_DATE_FORMAT}
            valueFormat="MM/DD/YYYY"
            style={{ width: '100%' }}
            disabled={!isEdit}
          />
        );
      default:
        return record[fieldName];
    }
  }, [handleContainerChange, isEdit, safeDayjs]);

  const containerColumns = useMemo(() => {
    const baseColumns = formConfig.containerInfo
      .filter((col) => !containerBottomFieldKeys.includes(col.dataIndex))
      .map((col) => {
      const fieldName = col.dataIndex;
      if (!fieldName) return col;

      return {
        ...col,
        shouldCellUpdate: (record, prevRecord) => {
          if (record.key === 'total' || prevRecord.key === 'total') {
            return record[fieldName] !== prevRecord[fieldName];
          }
          return (
            record[fieldName] !== prevRecord[fieldName] ||
            record.container_no !== prevRecord.container_no
          );
        },
        render: (value, record) => {
          const displayValue = CONTAINER_DATE_FIELDS.has(fieldName)
            ? formatDisplayDate(value)
            : value;

          if (!isEdit || record.key === 'total') return displayValue;
          return renderFieldWithInfo(
            displayValue,
            fieldName,
            record.container_no,
            isEdit,
            renderContainerInput(record, fieldName)
          );
        }
      };
    });

    if (!isEdit) return baseColumns;

    return [
      ...baseColumns,
      {
        title: 'Action',
        key: 'action',
        fixed: 'right',
        width: 120,
        render: (_, record) => (
          record.key !== 'total' &&
          <Popconfirm
            title="Delete container row?"
            description="This action will remove the current container row."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteContainer(record.id)}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
            </Button>
          </Popconfirm>
        ),
      }
    ];
  }, [containerBottomFieldKeys, formConfig.containerInfo, handleDeleteContainer, isEdit, renderContainerInput]);

  const renderContainerBottomInput = useCallback((record, fieldName) => {
    const rowIndex = record._rowIndex;
    if (rowIndex < 0) return null;

    if (fieldName === 'container_no') {
      return (
        <Input
          value={record.container_no || ''}
          disabled
        />
      );
    }

    if (fieldName === 'pickup_number') {
      return (
        <Input
          key={`${record.id || rowIndex}-${fieldName}-${record[fieldName] || ''}`}
          defaultValue={record[fieldName]}
          onChange={(e) => handleContainerChange(rowIndex, fieldName, e.target.value)}
          disabled={!isEdit}
        />
      );
    }

    if (fieldName === 'dd_note') {
      return (
        <Input
          key={`${record.id || rowIndex}-${fieldName}-${record[fieldName] || ''}`}
          defaultValue={record[fieldName]}
          onChange={(e) => handleContainerChange(rowIndex, fieldName, e.target.value)}
          disabled={!isEdit}
        />
      );
    }

    if (CONTAINER_DATE_FIELDS.has(fieldName)) {
      return (
        <DatePicker
          key={`${record.id || rowIndex}-${fieldName}-${record[fieldName] || ''}`}
          defaultValue={safeDayjs(record[fieldName], 'MM/DD/YYYY') || safeDayjs(record[fieldName], 'YYYY-MM-DD')}
          onChange={(_, dateString) => handleContainerChange(rowIndex, fieldName, dateString)}
          format={DISPLAY_DATE_FORMAT}
          valueFormat="MM/DD/YYYY"
          style={{ width: '100%' }}
          disabled={!isEdit}
        />
      );
    }

    return null;
  }, [handleContainerChange, isEdit, safeDayjs]);

  // 统计费用
  const sumValues = (...nums) =>
    nums.reduce((acc, val) => acc + Number(val || 0), 0);

  const subtractValues = (start, ...rest) =>
    rest.reduce((acc, val) => acc - Number(val || 0), Number(start || 0));

  const handlePoint = (value, digits = 2) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return 0;
    return Number(num.toFixed(digits));
  };

  // 处理费用变化
  const handleFeeChange = () => {
    const values = form.getFieldsValue();


    // 计算 total_cc = 所有费用的和
    const totalCC = sumValues(
      // values.hbl_freight_ppd,
      values.of_cc,
      values.ddc_devanning,
      values.baf_cc,
      values.arb_cfs,
      values.psc_pierpass,
      values.orc_port_security,
      values.gri_ctf,
      values.pss_exam_fee,
      values.acc_thc,
      values.ams_isf_fee, // 由ams_bl 改的
      values.it_fee,
      values.doc_cc,
      values.other_cc
    );

    // 计算 balance_to_origin = total_cc - mbl_cc - trucking_fee - profit_share - handling_fee
    const balanceToOrigin = subtractValues(
      totalCC,
      values.mbl_cc,
      values.trucking_fee,
      values.profit_share,
      values.handling_fee
    );


    // 计算 final_balance = 所有 balance_change_amount 的和 + balance_to_origin
    const finalBalance = sumValues(
      values.balance_change_amount1,
      values.balance_change_amount2,
      values.balance_change_amount3,
      balanceToOrigin
    );

    form.setFieldsValue({
      total_cc: handlePoint(totalCC),
      balance_to_origin: handlePoint(balanceToOrigin),
      final_balance: handlePoint(finalBalance),
    });
  };

  const handleCurrencyChange = (fieldName, value) => {
    form.setFieldsValue({ [fieldName]: value ?? 0 });
    handleFeeChange();
  };

  // =====================
  // 费用原始快照 Drawer
  // =====================
  const RESETTABLE_FEE_FIELDS = [
    { key: 'overdueAmount', label: 'Overdue Amount' },
    { key: 'hblFreightPpd', label: 'HB/L Freight PPD' },
    { key: 'ofCc', label: 'O/F CC' },
    { key: 'ddcDevanning', label: 'DDC Devanning' },
    { key: 'bafCc', label: 'BAF CC' },
    { key: 'arbCfs', label: 'ARB CFS' },
    { key: 'pscPierpass', label: 'PSC Pierpass' },
    { key: 'orcPortSecurity', label: 'ORC Port Security' },
    { key: 'griCtf', label: 'GRI CTF' },
    { key: 'pssExamFee', label: 'PSS Exam Fee' },
    { key: 'accThc', label: 'ACC THC' },
    { key: 'amsIsfFee', label: 'AMS ISF Fee' },
    { key: 'itFee', label: 'I.T Fee' },
    { key: 'docCc', label: 'DOC CC' },
    { key: 'otherCc', label: 'Other CC' },
    { key: 'totalCc', label: 'Total CC' },
    { key: 'mblCc', label: 'MBL CC' },
    { key: 'truckingFee', label: 'Trucking Fee' },
    { key: 'profitShare', label: 'Profit Share' },
    { key: 'handlingFee', label: 'Handling Fee' },
    { key: 'balanceToOrigin', label: 'Balance To Origin' },
    { key: 'balanceChangeAmount1', label: 'Balance Change Amt 1' },
    { key: 'balanceChangeAmount2', label: 'Balance Change Amt 2' },
    { key: 'balanceChangeAmount3', label: 'Balance Change Amt 3' },
    { key: 'finalBalance', label: 'Final Balance' },
  ];

  const ORDER_SNAPSHOT_FIELDS = [
    { key: 'hblNo', label: 'HBL No' },
    { key: 'mblNo', label: 'MBL No' },
    { key: 'bookingNo', label: 'Booking No' },
    { key: 'shipperCompany', label: 'Shipper' },
    { key: 'consigneeCompany', label: 'Consignee' },
    { key: 'notifyCompany', label: 'Notify Party' },
    { key: 'carrier', label: 'Carrier' },
    { key: 'vesselName', label: 'Vessel' },
    { key: 'voyage', label: 'Voyage' },
    { key: 'etd', label: 'ETD' },
    { key: 'etaPort', label: 'ETA Port' },
    { key: 'anDate', label: 'AN Date' },
    { key: 'releaseDate', label: 'Release Date' },
    { key: 'totalTeu', label: 'Total TEU' },
    { key: 'totalPackages', label: 'Total Packages' },
    { key: 'totalGrossWeight', label: 'Gross Weight' },
    { key: 'totalMeasurement', label: 'Measurement' },
    { key: 'status', label: 'Status' },
    { key: 'document', label: 'Document' },
    { key: 'pic', label: 'PIC' },
    { key: 'salesman', label: 'Salesman' },
  ];

  const CONTAINER_SNAPSHOT_FIELDS = [
    { key: 'containerNo', label: 'Container No' },
    { key: 'sealNo', label: 'Seal No' },
    { key: 'pickupNumber', label: 'Pickup No' },
    { key: 'teu', label: 'TEU' },
    { key: 'packages', label: 'Packages' },
    { key: 'grossWeight', label: 'Gross Weight' },
    { key: 'measurement', label: 'Measurement' },
    { key: 'lastFreeDay', label: 'Last Free Day' },
    { key: 'pickDate', label: 'Pick Date' },
    { key: 'returnDate', label: 'Return Date' },
    { key: 'pickStatus', label: 'Pick Status' },
    { key: 'returnStatus', label: 'Return Status' },
  ];

  const FEE_READONLY_FIELDS = [
    { key: 'balanceChange1', label: 'Balance Change 1' },
    { key: 'balanceChange2', label: 'Balance Change 2' },
    { key: 'balanceChange3', label: 'Balance Change 3' },
    { key: 'originNote', label: 'Origin Note' },
    { key: 'holdNote', label: 'Hold Note' },
  ];

  const [feeSnapshotDrawerOpen, setFeeSnapshotDrawerOpen] = useState(false);
  const [feeSnapshots, setFeeSnapshots] = useState([]); // HblSnapshotGroup[]
  const [feeSnapshotLoading, setFeeSnapshotLoading] = useState(false);
  const [feeSnapshotSelectedFields, setFeeSnapshotSelectedFields] = useState({}); // { [feesSnapshotId]: [fieldKey, ...] }
  const [feeSnapshotResetting, setFeeSnapshotResetting] = useState(false);

  const fetchFeeSnapshots = async () => {
    if (!hblNo) return;
    setFeeSnapshotLoading(true);
    try {
      const res = await apiClient.get(`/shipping-order/snapshots/${hblNo}`);
      const list = res?.data?.data || [];
      setFeeSnapshots(Array.isArray(list) ? list : []);
      setFeeSnapshotSelectedFields({});
    } catch (e) {
      console.error('fetch fee snapshots failed', e);
      messageApi.error('Failed to load fee snapshots');
      setFeeSnapshots([]);
    } finally {
      setFeeSnapshotLoading(false);
    }
  };

  const handleOpenFeeSnapshotDrawer = () => {
    setFeeSnapshotDrawerOpen(true);
    fetchFeeSnapshots();
  };

  const handleToggleFeeSnapshotField = (snapshotId, fieldKey) => {
    setFeeSnapshotSelectedFields(prev => {
      const current = prev[snapshotId] || [];
      const next = current.includes(fieldKey)
        ? current.filter(k => k !== fieldKey)
        : [...current, fieldKey];
      return { ...prev, [snapshotId]: next };
    });
  };

  const handleSelectAllFeeSnapshotFields = (snapshotId, checked) => {
    setFeeSnapshotSelectedFields(prev => ({
      ...prev,
      [snapshotId]: checked ? RESETTABLE_FEE_FIELDS.map(f => f.key) : [],
    }));
  };

  const handleResetFeeSnapshot = async (snapshotId) => {
    const fields = feeSnapshotSelectedFields[snapshotId] || [];
    if (fields.length === 0) {
      messageApi.warning('Please select at least one field to reset');
      return;
    }
    setFeeSnapshotResetting(true);
    try {
      const res = await apiClient.post('/shipping-order/snapshots/fees/reset', { id: snapshotId, fields });
      if (res?.data?.code === 200) {
        const resetFields = res.data.data || [];
        messageApi.success(`Reset ${resetFields.length} field(s) successfully`);
        setFeeSnapshotSelectedFields(prev => ({ ...prev, [snapshotId]: [] }));
        // 刷新快照列表和表单数据
        fetchFeeSnapshots();
        fetchDetailData();
      } else {
        messageApi.error(res?.data?.message || 'Reset failed');
      }
    } catch (e) {
      console.error('reset fee snapshot failed', e);
      messageApi.error('Reset failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setFeeSnapshotResetting(false);
    }
  };




  // 可选：打开面板时按 HBL 拉取邮件列表
  useEffect(() => {
    if (!showEmailPanel) return;
    const fetchEmails = async () => {
      setEmailLoading(true);
      setEmailError(null);
      try {
        const id = workorderDetailData?.id;
        if (!id) {
          setEmails([]);
          setSelectedEmail(null);
          return;
        }
        const res = await apiClient.get(`/shipping-order/emails-detail?workorderId=${id}`);
        const { data } = res?.data || {};
        const list = Array.isArray(data) ? data : [];
        setEmails(list);
        setSelectedEmail(list[0] || null);
      } catch (e) {
        console.error('fetch emails failed', e);
        setEmailError('Load emails failed');
        setEmails([]);
        setSelectedEmail(null);
      } finally {
        setEmailLoading(false);
      }
    };
    fetchEmails();
  }, [showEmailPanel, hblNo]);


  const fetchEmailAttachments = useCallback(async () => {
    setAttachmentLoading(true);
    setAttachmentError(null);
    try {
      const id = workorderDetailData?.id;
      if (!id) {
        setAttachments([]);
        return;
      }
      const res = await apiClient.get(`/shipping-order/email-attachment-pages/${id}`);
      const { data } = res?.data || {};
      const rawList = Array.isArray(data) ? data : [];
      const list = rawList.map((item, index) => {
        const fileName = item?.FileName || item?.fileName || item?.file_name || '';
        const ossUrl = item?.OssUrl || item?.ossUrl || item?.oss_url || '';
        return {
          ...item,
          file_name: fileName,
          oss_url: ossUrl,
          size_kb: item?.size_kb ?? null,
          // 新接口可能没有 id，生成稳定 key 供列表选中/渲染使用
          _rowKey: `${fileName || 'file'}-${ossUrl || 'url'}-${index}`,
        };
      });
      setAttachments(list);
      setSelectedArchiveAttachmentKeys([]);
      if (activeAttachmentTab === 'attachments' && list.length > 0) {
        setSelectedAttachment(list[0]);
      }
    } catch (e) {
      console.error('fetch email attachments failed', e);
      setAttachmentError('Load email attachments failed');
      setAttachments([]);
      setSelectedArchiveAttachmentKeys([]);
    } finally {
      setAttachmentLoading(false);
    }
  }, [activeAttachmentTab, workorderDetailData?.id]);

  const fetchUploadedFiles = useCallback(async () => {
    setUploadedFilesLoading(true);
    setUploadedFilesError(null);
    try {
      if (!hblNo) {
        uploadedFilesRef.current = [];
        return;
      }
      const res = await apiClient.get(`/shipping-order/attachments/${hblNo}`);
      const { data } = res?.data || {};
      const list = Array.isArray(data) ? data : [];
      uploadedFilesRef.current = list;
      setUploadedFiles(list); // sync display state on initial load
    } catch (e) {
      console.error('fetch uploaded files failed', e);
      setUploadedFilesError('Load uploaded files failed');
      uploadedFilesRef.current = [];
    } finally {
      setUploadedFilesLoading(false);
    }
  }, [hblNo]);

  // Ref-only refresh for upload completion — no parent state change except uploadedFiles display data
  const refreshUploadedFiles = useCallback(async () => {
    try {
      if (!hblNo) return;
      const res = await apiClient.get(`/shipping-order/attachments/${hblNo}`);
      const { data } = res?.data || {};
      const list = Array.isArray(data) ? data : [];
      uploadedFilesRef.current = list;
      setUploadedFiles(list); // only sync display data — no loading state
    } catch (e) {
      console.error('refresh uploaded files failed', e);
    }
  }, [hblNo]);

  const fetchAnAttachments = useCallback(async () => {
    setAnAttachmentsLoading(true);
    setAnAttachmentsError(null);
    try {
      if (!hblNo) {
        anAttachmentsRef.current = [];
        return;
      }
      const res = await apiClient.get(`/shipping-order/an-attachments/${hblNo}?emailType=${anEmailType}`);
      const { data } = res?.data || {};
      const list = Array.isArray(data) ? data : [];
      anAttachmentsRef.current = list;
      if (list.length > 0 && activeAttachmentTab === 'an_attachments') {
        setSelectedAnAttachment(list[0]);
      }
    } catch (e) {
      console.error('fetch an attachments failed', e);
      setAnAttachmentsError('Load AN/PU attachments failed');
      anAttachmentsRef.current = [];
    } finally {
      setAnAttachmentsLoading(false);
    }
  }, [activeAttachmentTab, anEmailType, hblNo]);

  useEffect(() => {
    if (!showAttachmentPanel) return;
    fetchEmailAttachments();
  }, [showAttachmentPanel, fetchEmailAttachments]);

  useEffect(() => {
    if (!showAttachmentPanel) return;
    fetchUploadedFiles();
  }, [showAttachmentPanel, fetchUploadedFiles]);

  useEffect(() => {
    if (!showAttachmentPanel) return;
    fetchAnAttachments();
  }, [showAttachmentPanel, fetchAnAttachments]);

  // Sync display state from refs only when panel opens or tab changes — avoids re-rendering entire component on upload/delete
  useEffect(() => {
    if (showAttachmentPanel && activeAttachmentTab === 'files') {
      setUploadedFiles(uploadedFilesRef.current);
    }
  }, [showAttachmentPanel, activeAttachmentTab]);

  useEffect(() => {
    if (showAttachmentPanel && activeAttachmentTab === 'an_attachments') {
      setAnAttachments(anAttachmentsRef.current);
    }
  }, [showAttachmentPanel, activeAttachmentTab]);


  // 获取文件类型图标
  const getFileIcon = (fileName) => {
    const fileType = fileName?.split('.').pop()?.toLowerCase();
    const iconStyle = { fontSize: '48px' };

    if (fileType === 'pdf') {
      return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
    } else if (fileType === 'doc' || fileType === 'docx') {
      return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
    } else if (fileType === 'xls' || fileType === 'xlsx') {
      return <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
    } else if (fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png' || fileType === 'gif') {
      return <FileImageOutlined style={{ ...iconStyle, color: '#722ed1' }} />;
    } else if (fileType === 'zip' || fileType === 'rar') {
      return <FileZipOutlined style={{ ...iconStyle, color: '#fa8c16' }} />;
    } else if (fileType === 'txt') {
      return <FileTextOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />;
    } else {
      return <FileOutlined style={{ ...iconStyle, color: '#8c8c8c' }} />;
    }
  };







  // 预览附件（使用 iframe）
  const renderAttachmentPreview = () => {
    // 检查是否是 AN/PU 附件的预览
    if (activeAttachmentTab === 'an_attachments' && selectedAnAttachment) {
      const ossUrl = selectedAnAttachment.ossUrl || '';
      const fileName = selectedAnAttachment.fileName || '';

      if (!ossUrl) {
        return <div style={{ textAlign: 'center' }}><p style={{ color: '#999' }}>Attachments Not Upload</p></div>;
      }

      const nameOrUrl = (fileName || ossUrl);
      const lower = nameOrUrl.toLowerCase();
      const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lower.endsWith(ext));
      const isPdf = lower.endsWith('.pdf');

      if (isImage) {
        return (
          <Image
            src={ossUrl}
            alt={fileName}
            width="100%"
            style={{ height: 'auto', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            preview={{ mask: 'Click Preview' }}
            fallback=""
          />
        );
      }

      if (isPdf) {
        const hasPaiOcr = ossUrl.includes('pai-ocr');

        if (hasPaiOcr) {
          return (
            <iframe
              src={ossUrl}
              style={{
                width: '100%',
                height: '100%',
                minHeight: 600,
                border: 'none'
              }}
              title={fileName}
            />
          );
        }

        return (
          <div style={{
            width: '100%',
            height: '100%',
            minHeight: 600,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #d9d9d9',
            borderRadius: 4,
            padding: 24
          }}>
            {getFileIcon(fileName)}
            <p style={{ color: '#999', margin: '12px 0 16px' }}>
              PDF file preview. Click the button below to view or download.
            </p>
            <div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = ossUrl;
                  a.download = fileName || 'file.pdf';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                style={{ marginRight: 8 }}
              >
                Download
              </Button>
              <Button onClick={() => window.open(ossUrl, '_blank')}>
                Open in New Window
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div style={{
          width: '100%',
          height: '100%',
          minHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #d9d9d9',
          borderRadius: 4,
          padding: 24
        }}>
          {getFileIcon(fileName)}
          <p style={{ color: '#999', margin: '12px 0 16px' }}>
            The file cannot be previewed in the page, please download or open in a new window to view.
          </p>
          <div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                const a = document.createElement('a');
                a.href = ossUrl;
                a.download = fileName || 'file';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
              style={{ marginRight: 8 }}
            >
              Download
            </Button>
            <Button onClick={() => window.open(ossUrl, '_blank')}>
              Open in New Window
            </Button>
          </div>
        </div>
      );
    }

    if (!selectedAttachment) return null;

    // 兼容两种数据结构：attachments 和 uploadedFiles
    const ossUrl = selectedAttachment.oss_url || selectedAttachment.ossUrl || '';
    const fileName = selectedAttachment.file_name || selectedAttachment.fileName || '';

    if (!ossUrl) {
      return <div style={{ textAlign: 'center' }}><p style={{ color: '#999' }}>Attachments Not Upload</p></div>;
    }

    const nameOrUrl = (fileName || ossUrl);
    const lower = nameOrUrl.toLowerCase();
    const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lower.endsWith(ext));
    const isPdf = lower.endsWith('.pdf');

    if (isImage) {
      return (
        <Image
          src={ossUrl}
          alt={fileName}
          width="100%"
          style={{ height: 'auto', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          preview={{ mask: 'Click Preview' }}
          fallback=""
        />
      );
    }

    // PDF 文件：检查是否包含 pai-ocr，如果包含则使用 iframe 预览
    if (isPdf) {
      const hasPaiOcr = ossUrl.includes('pai-ocr');

      if (hasPaiOcr) {
        return (
          <iframe
            src={ossUrl}
            style={{
              width: '100%',
              height: '100%',
              minHeight: 600,
              border: 'none'
            }}
            title={fileName}
          />
        );
      }

      return (
        <div style={{
          width: '100%',
          height: '100%',
          minHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #d9d9d9',
          borderRadius: 4,
          padding: 24
        }}>
          {getFileIcon(fileName)}
          <p style={{ color: '#999', margin: '12px 0 16px' }}>
            PDF file preview. Click the button below to view or download.
          </p>
          <div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                const a = document.createElement('a');
                a.href = ossUrl;
                a.download = fileName || 'file.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
              style={{ marginRight: 8 }}
            >
              Download
            </Button>
            <Button onClick={() => window.open(ossUrl, '_blank')}>
              Open in New Window
            </Button>
          </div>
        </div>
      );
    }

    // 其他类型：统一兜底
    return (
      <div style={{
        width: '100%',
        height: '100%',
        minHeight: 600,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed #d9d9d9',
        borderRadius: 4,
        padding: 24
      }}>
        {getFileIcon(fileName)}
        <p style={{ color: '#999', margin: '12px 0 16px' }}>
          The file cannot be previewed in the page, please download or open in a new window to view.
        </p>
        <div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              const a = document.createElement('a');
              a.href = ossUrl;
              a.download = fileName || 'file';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
            style={{ marginRight: 8 }}
          >
            Download
          </Button>
          <Button onClick={() => window.open(ossUrl, '_blank')}>
            Open in New Window
          </Button>
        </div>
      </div>
    );
  };



  const handleArchiveEmailAttachments = async () => {
    if (!hblNo) {
      messageApi.warning('HBL No. is missing');
      return;
    }
    if (!attachments.length) {
      messageApi.info('No email attachments to archive');
      return;
    }
    if (!selectedArchiveAttachmentKeys.length) {
      messageApi.info('Please select attachments to archive');
      return;
    }

    const archiveItems = attachments
      .filter((item, index) => {
        const itemId = item._rowKey || item.id || item.attachment_id || index;
        return selectedArchiveAttachmentKeys.includes(itemId);
      })
      .map((item) => ({
        OssUrl: item.OssUrl ?? item.oss_url ?? item.ossUrl ?? '',
        FileName: item.FileName ?? item.file_name ?? item.fileName ?? '',
      }))
      .filter((item) => String(item.FileName || '').trim() !== '');

    if (!archiveItems.length) {
      messageApi.info('No valid email attachments to archive');
      return;
    }

    const messageKey = 'archive-email-attachments';
    messageApi.open({
      key: messageKey,
      type: 'loading',
      content: `Archiving ${archiveItems.length} attachment(s)...`,
      duration: 0,
    });

    setArchiveEmailAttachmentsLoading(true);
    try {
      const payload = {
        hbl_no: hblNo,
        attachments: archiveItems,
      };

      const response = await attachmentApi.archiveEmailAttachments(payload);
      const responseData = response?.data || {};
      if (responseData.code === 200) {
        const result = responseData.data || {};
        setSelectedArchiveAttachmentKeys([]);
        messageApi.open({
          key: messageKey,
          type: 'success',
          content: `Archived ${result.inserted_count ?? 0} attachment(s), skipped ${result.skipped_count ?? 0}`,
        });
        await fetchUploadedFiles();
      } else {
        messageApi.open({
          key: messageKey,
          type: 'error',
          content: responseData.message || 'Archive failed',
        });
      }
    } catch (error) {
      console.error('archive email attachments failed', error);
      messageApi.open({
        key: messageKey,
        type: 'error',
        content: error.response?.data?.message || 'Archive failed',
      });
    } finally {
      setArchiveEmailAttachmentsLoading(false);
    }
  };

  const handleDeleteUploadedAttachment = async (item) => {
    const attachmentId = item?.id || item?.attachment_id;
    if (!attachmentId || Number(attachmentId) <= 0) {
      messageApi.warning('Invalid attachment id');
      return;
    }

    try {
      const res = await apiClient.delete(`/shipping-order/attachments/${attachmentId}`);
      const responseData = res?.data || {};
      if (responseData?.code === 200) {
        messageApi.success(responseData?.message || 'Attachment deleted');
        // Optimistic update on both ref and display state
        uploadedFilesRef.current = uploadedFilesRef.current.filter(a => {
          const aid = a?.id || a?.attachment_id;
          return aid && Number(aid) !== Number(attachmentId);
        });
        setUploadedFiles(prev => prev.filter(a => {
          const aid = a?.id || a?.attachment_id;
          return aid && Number(aid) !== Number(attachmentId);
        }));

        const selectedId = selectedAttachment?.id || selectedAttachment?.attachment_id;
        if (selectedId && Number(selectedId) === Number(attachmentId)) {
          setSelectedAttachment(null);
        }
      } else {
        messageApi.error(responseData?.message || 'Delete failed');
      }
    } catch (error) {
      messageApi.error(error?.response?.data?.message || error?.message || 'Delete failed');
    }
  };

  const handleDeleteAnAttachment = async (item) => {
    const attachmentId = item?.id;
    if (!attachmentId || Number(attachmentId) <= 0) {
      messageApi.warning('Invalid AN/PU attachment id');
      return;
    }

    try {
      const res = await apiClient.post('/shipping-order/an-attachments/batch-delete', {
        ids: [Number(attachmentId)],
      });
      const responseData = res?.data || {};
      if (responseData?.code === 200) {
        const result = responseData?.data || {};
        messageApi.success(
          `Deleted ${result.deletedCount ?? 0}, skipped ${result.skippedCount ?? 0}`
        );
        // Optimistic update on both ref and display state
        anAttachmentsRef.current = anAttachmentsRef.current.filter(a => a?.id && Number(a.id) !== Number(attachmentId));
        setAnAttachments(prev => prev.filter(a => a?.id && Number(a.id) !== Number(attachmentId)));

        const selectedId = selectedAnAttachment?.id;
        if (selectedId && Number(selectedId) === Number(attachmentId)) {
          setSelectedAnAttachment(null);
        }
      } else {
        messageApi.error(responseData?.message || 'Delete failed');
      }
    } catch (error) {
      messageApi.error(error?.response?.data?.message || error?.message || 'Delete failed');
    }
  };



  // 获取字段说明信息
  const fetchFieldDescription = async (fieldName) => {
    if (!fieldName) return;

    fieldName = toCamelCase(fieldName); // 转换为驼峰命名
    try {
      // 调用接口获取字段说明信息
      const response = await apiClient.get(`/shipping-field-change/query?shippingOrderId=${workorderDetailData?.id}&fieldName=${fieldName}`);
      const description = response.data.data || [];

      // 更新字段说明状态
      setFieldDescriptions(description);

      // 显示modal
      setIsfieldDescShow(true);
    } catch (error) {
      console.error('获取字段说明失败:', error);
      // 设置错误信息
      setFieldDescriptions([]);
      // 显示modal
      setIsfieldDescShow(true);
    } finally {
      // 清除加载状态
      console.log('清除加载状态');
    }
  };

  // 获取容器字段更改记录
  const fetchContainerFieldDescription = async (fieldName, containerNo) => {
    if (!fieldName || !containerNo) return;

    fieldName = toCamelCase(fieldName); // 转换为驼峰命名
    try {
      // 调用接口获取容器字段说明信息
      const response = await apiClient.get(`/shipping-field-change/query?shippingOrderId=${workorderDetailData?.id}&fieldName=${fieldName}&containNo=${containerNo}`);
      const description = response.data.data || [];

      // 更新容器字段说明状态
      setContainerFieldDescriptions(description);

      // 显示modal
      setIsContainerFieldDescShow(true);
    } catch (error) {
      console.error('获取容器字段说明失败:', error);
      // 设置错误信息
      setContainerFieldDescriptions([]);
      // 显示modal
      setIsContainerFieldDescShow(true);
    } finally {
      // 清除加载状态
      console.log('清除加载状态');
    }
  };

  // 渲染带有Info图标的字段
  function renderFieldWithInfo(value, fieldName, containerNo, isEdit, inputComponent) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ flex: 1 }}>
          {isEdit ? inputComponent : value}
        </div>
        {/* <InfoCircleOutlined
          style={{ color: '#1f63fb', cursor: 'pointer', fontSize: '14px' }}
          onClick={() => fetchContainerFieldDescription(fieldName, containerNo)}
        /> */}
      </div>
    );
  }



  // A/N
  const [showAnPanel, setShowAnPanel] = useState(false); // Drawer的可见性
  const [selectedAnTemplate, setSelectedAnTemplate] = useState({ value: 2, label: 'AN-US-FCL' });
  const [pdfUrl, setPdfUrl] = useState(null); // 下载的PDF模板
  const [generateanLoading, setGenerateanLoading] = useState(false);
  const [sendanLoading, setSendanLoading] = useState(false); // 发送A/N loading
  const [anEmailRecords, setAnEmailRecords] = useState([]); // A/N 邮件发送记录
  const [anRecordsLoading, setAnRecordsLoading] = useState(false); // A/N 邮件发送记录加载状态




  // PDF 显示面板
  const [showPdfPanel, setShowPdfPanel] = useState(false);
  const [pdfDisplayUrl, setPdfDisplayUrl] = useState(null);
  const [showVersionSnapshotDrawer, setShowVersionSnapshotDrawer] = useState(false);
  const [versionSnapshots, setVersionSnapshots] = useState([]);
  const [versionSnapshotsLoading, setVersionSnapshotsLoading] = useState(false);
  const [showTrackNoteModal, setShowTrackNoteModal] = useState(false);
  const [trackNoteData, setTrackNoteData] = useState([]);
  const trackNoteDraftRef = useRef([]);
  const [trackNoteLoading, setTrackNoteLoading] = useState(false);
  const [trackNoteSaving, setTrackNoteSaving] = useState(false);

  const formatSnapshotValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  };

  const fetchVersionSnapshots = useCallback(async () => {
    const targetHblNo = workorderDetailData?.hbl_no || hblNo;
    if (!targetHblNo) {
      messageApi.warning('HBL No. is missing');
      return;
    }

    try {
      setVersionSnapshotsLoading(true);
      const response = await workorderApi.getVersionSnapshots(targetHblNo, {
        sortBy: 'version',
        order: 'desc',
      });

      if (response?.data?.code === 200) {
        setVersionSnapshots(response?.data?.data || []);
      } else {
        setVersionSnapshots([]);
        messageApi.error(response?.data?.message || 'Failed to load version snapshots');
      }
    } catch (error) {
      console.error('Fetch version snapshots failed', error);
      setVersionSnapshots([]);
      messageApi.error(error?.response?.data?.message || error?.message || 'Failed to load version snapshots');
    } finally {
      setVersionSnapshotsLoading(false);
    }
  }, [hblNo, messageApi, workorderDetailData?.hbl_no]);

  const fetchTrackNotes = useCallback(async () => {
    const targetHblNo = workorderDetailData?.hbl_no || hblNo;
    if (!targetHblNo) {
      messageApi.warning('HBL No. is missing');
      return;
    }

    try {
      setTrackNoteLoading(true);
      const response = await workorderApi.getTrackNotes(targetHblNo);
      if (response?.data?.code === 200) {
        const records = (response?.data?.data || []).map((item, index) => ({
          id: item?.id || `track_note_${index}_${Date.now()}`,
          content: item?.content || '',
          todo_date: item?.todoDate || '',
          username: item?.username || '',
          update_time: item?.updateTime || '',
        }));
        trackNoteDraftRef.current = records;
        setTrackNoteData(records);
      } else {
        trackNoteDraftRef.current = [];
        setTrackNoteData([]);
        messageApi.error(response?.data?.message || 'Failed to load track notes');
      }
    } catch (error) {
      console.error('Fetch track notes failed', error);
      trackNoteDraftRef.current = [];
      setTrackNoteData([]);
      messageApi.error(error?.response?.data?.message || error?.message || 'Failed to load track notes');
    } finally {
      setTrackNoteLoading(false);
    }
  }, [hblNo, messageApi, workorderDetailData?.hbl_no]);

  const handleOpenTrackNoteModal = useCallback(async () => {
    setShowTrackNoteModal(true);
    await fetchTrackNotes();
  }, [fetchTrackNotes]);

  const handleTrackNoteChange = useCallback((index, field, value) => {
    const current = [...(trackNoteDraftRef.current || [])];
    if (!current[index]) return;
    current[index] = { ...current[index], [field]: value };
    trackNoteDraftRef.current = current;
  }, []);

  const handleAddTrackNote = useCallback(() => {
    const next = [
      ...(trackNoteDraftRef.current || []),
      {
        id: `track_note_${Date.now()}_${Math.round(Math.random() * 10000)}`,
        content: '',
        todo_date: '',
        username: '',
        update_time: '',
      }
    ];
    trackNoteDraftRef.current = next;
    setTrackNoteData(next);
  }, []);

  const handleDeleteTrackNote = useCallback((id) => {
    const next = (trackNoteDraftRef.current || []).filter((item) => item.id !== id);
    trackNoteDraftRef.current = next;
    setTrackNoteData(next);
  }, []);

  const handleSaveTrackNotes = useCallback(async () => {
    const targetHblNo = workorderDetailData?.hbl_no || hblNo;
    if (!targetHblNo) {
      messageApi.warning('HBL No. is missing');
      return;
    }

    const payloadList = (trackNoteDraftRef.current || [])
      .map((item) => {
        let todoDate = item?.todo_date || null;
        if (todoDate && dayjs.isDayjs(todoDate)) {
          todoDate = todoDate.format('YYYY-MM-DD');
        } else if (typeof todoDate === 'string' && todoDate.includes('/')) {
          const parsed = dayjs(todoDate, 'MM/DD/YYYY');
          todoDate = parsed.isValid() ? parsed.format('YYYY-MM-DD') : todoDate;
        }

        return {
          content: (item?.content || '').trim(),
          todo_date: todoDate || null,
        };
      })
      .filter((item) => item.content);

    if (!payloadList.length) {
      messageApi.warning('At least one non-empty track note is required');
      return;
    }

    try {
      setTrackNoteSaving(true);
      const response = await workorderApi.saveTrackNotes({
        hbl_no: targetHblNo,
        track_note_content: payloadList,
      });

      if (response?.data?.code === 200) {
        const records = (response?.data?.data || []).map((item, index) => ({
          id: item?.id || `track_note_${index}_${Date.now()}`,
          content: item?.content || '',
          todo_date: item?.todoDate || '',
          username: item?.username || '',
          update_time: item?.updateTime || '',
        }));
        trackNoteDraftRef.current = records;
        setTrackNoteData(records);
        messageApi.success(response?.data?.message || 'Track notes saved');
        setShowTrackNoteModal(false);
      } else {
        messageApi.error(response?.data?.message || 'Failed to save track notes');
      }
    } catch (error) {
      console.error('Save track notes failed', error);
      messageApi.error(error?.response?.data?.message || error?.message || 'Failed to save track notes');
    } finally {
      setTrackNoteSaving(false);
    }
  }, [hblNo, messageApi, workorderDetailData?.hbl_no]);

  const trackNoteTableDataSource = useMemo(() => {
    return trackNoteData.map((item, index) => ({
      ...item,
      key: item.id || index,
      _rowIndex: index,
    }));
  }, [trackNoteData]);

  const trackNoteColumns = useMemo(() => ([
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      width: 360,
      render: (value, record) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          defaultValue={value}
          onChange={(e) => handleTrackNoteChange(record._rowIndex, 'content', e.target.value)}
        />
      ),
    },
    {
      title: 'Todo Date',
      dataIndex: 'todo_date',
      key: 'todo_date',
      width: 160,
      render: (value, record) => (
        <DatePicker
          defaultValue={safeDayjs(value, 'YYYY-MM-DD') || safeDayjs(value, 'MM/DD/YYYY')}
          onChange={(_, dateString) => handleTrackNoteChange(record._rowIndex, 'todo_date', dateString)}
          format={DISPLAY_DATE_FORMAT}
          valueFormat="YYYY-MM-DD"
          allowClear
        />
      ),
    },
    {
      title: 'User',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: 'Updated',
      dataIndex: 'update_time',
      key: 'update_time',
      width: 180,
      render: (value) => value ? formatDisplayDate(value) : '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          danger
          onClick={() => handleDeleteTrackNote(record.id)}
          icon={<DeleteOutlined />}
        >
        </Button>
      ),
    }
  ]), [handleDeleteTrackNote, handleTrackNoteChange, safeDayjs]);


  // 生成PDF
  const handleGeneratePdf = async () => {
    setGenerateanLoading(true);
    if (!hblNo) {
      messageApi.error('HBL No. is missing');
      return;
    }
    try {
      const { data } = await apiClient.post('/shipping-order/generate-pdf', {
        hblNo,
        modelId: `pmodel-${selectedAnTemplate?.value}`,
      });
      if (data?.code === 200) {
        setGenerateanLoading(false);
        setPdfUrl(data?.data);
        messageApi.success('PDF Generate Success');

      }
      else {
        setGenerateanLoading(false);
        messageApi.error(data?.message);
      }
    }
    catch (error) {
      setGenerateanLoading(false);
      console.error('Download PDF failed', error);
      messageApi.error('PDF download failed');
    }
  };






  // 查询 A/N 邮件发送记录（打开 Drawer / Refresh / 发送成功后调用）
  const fetchAnEmailRecords = useCallback(async () => {
    if (!hblNo) return;
    try {
      setAnRecordsLoading(true);
      const response = await apiClient.post('/an-email-record/query', {
        page: 1,
        size: 99,
        hblNo,
      });
      const { records } = response?.data?.data || {};
      setAnEmailRecords(records || []);
    } catch (error) {
      console.error('Fetch A/N email records failed', error);
      setAnEmailRecords([]);
    } finally {
      setAnRecordsLoading(false);
    }
  }, [hblNo]);

  useEffect(() => {
    if (showAnPanel && hblNo) {
      setAnEmailRecords([]);
      fetchAnEmailRecords();
    }
  }, [showAnPanel, hblNo, fetchAnEmailRecords]);
  // 发送A/N邮件
  const handleSendAnEmail = async () => {
    if (!hblNo) {
      messageApi.error('HBL No. is missing');
      return;
    }
    if (!pdfUrl) {
      messageApi.warning('Please Generate an A/N PDF first');
      return;
    }
    try {
      setSendanLoading(true);
      const params = {
        hblNo: hblNo,
        modelId: `pmodel-${selectedAnTemplate?.value}`,
        fileUrl: pdfUrl || "",
        consigneeEmail: form.getFieldValue('consignee_email') || "",
        notifyEmail: form.getFieldValue('notify_email') || ""
      };
      const response = await apiClient.post(`/shipping-order/send-an-email`, params);
      if (response.data.code === 200) {
        messageApi.success(response.data.data || 'Email sent successfully');
        await fetchAnEmailRecords();
      } else {
        messageApi.error(response.data.message || 'Email send failed');
      }
      setSendanLoading(false);
    } catch (error) {
      console.error('Send A/N email failed', error);
      messageApi.error('Email send failed: ' + (error.response?.data?.message || error.message));
      setSendanLoading(false);
    }
  };




  // 保存工单数据
  const [saveLoading, setSaveLoading] = useState(false);
  const canTriggerSaveShortcut = isEdit && ((userInfo && userInfo.username === 'admin') || workorderDetailData?.source_flag !== 'pms');
  const handleSave = async () => {
    try {
      setSaveLoading(true);
      // 获取表单数据
      const formData = await form.validateFields();

      // 格式化日期字段为 YYYY-MM-DD
      const formattedData = { ...formData };
      Object.keys(formattedData).forEach(key => {
        // 检查是否为日期字段
        const isDateField = formConfig.workorderInfo.concat(
          formConfig.entrustmentInfo,
          formConfig.shipperInfo,
          formConfig.consigneeInfo,
          formConfig.notifyPartyInfo,
          formConfig.container_cargo,
          formConfig.shipmentUpdateInfo,
          formConfig.releaseInfo,
          formConfig.otherInfo
        ).some(field => field.name === key && field.type === 'date');

        // 处理 表单 中的日期字段
        if (isDateField && formattedData[key]) {
          // 如果是dayjs对象，格式化为YYYY-MM-DD
          if (dayjs.isDayjs(formattedData[key])) {
            formattedData[key] = formattedData[key].format('YYYY-MM-DD');
          }
          // 如果是ISO格式的日期字符串，转换为YYYY-MM-DD
          else if (typeof formattedData[key] === 'string' && formattedData[key].endsWith('Z') && formattedData[key].includes('T')) {
            try {
              const date = dayjs(formattedData[key]);
              if (date.isValid()) {
                formattedData[key] = date.format('YYYY-MM-DD');
              }
            } catch (e) {
              console.warn('Date formatting error:', e);
            }
          }
        }
      });

      ['hbl_no', 'mbl_no', 'booking_no', 'ams_bl'].forEach((fieldName) => {
        if (typeof formattedData[fieldName] === 'string') {
          formattedData[fieldName] = formattedData[fieldName].trim();
        }
      });


      const sourceContainers = containerDraftRef.current || [];
      const resContainerData = sourceContainers ? sourceContainers.map(container => {
        const formattedContainer = { ...container };
        const parseContainerDate = (value) => {
          if (dayjs.isDayjs(value)) {
            return value.format('YYYY-MM-DD');
          }

          if (typeof value === 'string' && value.includes('/')) {
            const date = dayjs(value, ['MM/DD/YYYY', 'MM/DD/YY'], true);
            if (date.isValid()) {
              return date.format('YYYY-MM-DD');
            }
          }

          return value;
        };

        // 确保日期字段格式为 YYYY-MM-DD
        if (formattedContainer.pickup_date) {
          formattedContainer.pickup_date = parseContainerDate(formattedContainer.pickup_date);
        }

        if (formattedContainer.return_date) {
          formattedContainer.return_date = parseContainerDate(formattedContainer.return_date);
        }

        if (formattedContainer.last_free_day) {
          formattedContainer.last_free_day = parseContainerDate(formattedContainer.last_free_day);
        }

        return formattedContainer;
      }) : [];
      // 准备请求数据
      const requestData = {
        ...formattedData,
        containers: resContainerData,
      };

      // 发送更新请求
      const response = await apiClient.put('/shipping-order/update', requestData);

      if (response.data.code === 200) {
        // 保存成功后，同步容器state，避免退出编辑态后回退到旧值
        setContainerData(resContainerData);
        containerDraftRef.current = resContainerData;
        messageApi.success(response.data.message || 'Save Success');
        setSaveLoading(false);
        // 可以在这里刷新数据或执行其他操作
      } else {
        messageApi.error(response.data.message || 'Save Failure');
        setIsEdit(true);
        setSaveLoading(false);
      }
    } catch (error) {
      console.error('Save Failure', error);
      messageApi.error('Save Failure: ' + (error.response?.data?.message || error.message));
      setIsEdit(true);
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
      if (!canTriggerSaveShortcut || saveLoading) return;

      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const tagName = target.tagName.toLowerCase();
      if (tagName === 'textarea' || target.isContentEditable) return;
      if (target.closest('.ant-select-dropdown') || target.closest('.ant-picker-dropdown') || target.closest('.ant-popover')) return;

      event.preventDefault();
      handleSave();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canTriggerSaveShortcut, handleSave, saveLoading]);




  // 更新附件备注
  const handleUpdateRemark = async (id, remark, type) => {
    if (!id) return false;
    setSavingRemark(true);
    try {
      const url = type === 'file'
        ? '/shipping-order/attachment/remark'
        : '/shipping-order/an-attachment/remark';
      const response = await apiClient.put(url, {
        id,
        remark: remark || null
      });
      if (response.data?.code === 200) {
        messageApi.success('Remark updated successfully');
        if (type === 'file') {
          uploadedFilesRef.current = uploadedFilesRef.current.map(item =>
            item.id === id ? { ...item, remark } : item
          );
          setUploadedFiles(prev => prev.map(item =>
            item.id === id ? { ...item, remark } : item
          ));
        } else {
          anAttachmentsRef.current = anAttachmentsRef.current.map(item =>
            item.id === id ? { ...item, remark } : item
          );
          setAnAttachments(prev => prev.map(item =>
            item.id === id ? { ...item, remark } : item
          ));
        }
        setEditingRemarkId(null);
        setEditingRemarkType('');
        return true;
      } else {
        messageApi.error(response.data?.message || 'Update failed');
        return false;
      }
    } catch (error) {
      messageApi.error('Failed to update remark');
      return false;
    } finally {
      setSavingRemark(false);
    }
  };




  // 整个组件的 Dom
  return (
    <div className='workorderdetail'>
      {contextHolder}

      <div className="workorderdetail-fixedtoolbtn">

        <div className="workorderdetail-toolbtn-left">
          <Button
            // type='dashed'
            size='middle'
            onClick={() => navigate('/workorder')}
            style={{
              fontSize: 12
            }}
          >
            Return to Workorder
          </Button>



          {/* 编辑 */}
          {!isEdit &&
            ((userInfo && userInfo.username === 'admin') || (userInfo && userInfo.username !== 'admin' && workorderDetailData?.source_flag !== 'pms'))
            && (
              <Button
                type='primary'
                size="middle"
                style={{ marginRight: 8 }}
                onClick={() => setIsEdit(true)}
              >
                Edit
              </Button>
            )}


          {/* 取消 */}
          {isEdit && (userInfo && userInfo.username === 'admin' || workorderDetailData?.source_flag !== 'pms') &&
            <Button
              // type='dashed'
              size="middle"
              style={{ marginRight: 8 }}
              onClick={() => {
                fetchDetailData();
                setIsEdit(false)
              }}
            >
              Cancel
            </Button>
          }
          {/* 保存 */}
          {isEdit && (userInfo && userInfo.username === 'admin' || workorderDetailData?.source_flag !== 'pms') &&
            <SaveConfirmButton
              type='primary'
              size="middle"
              style={{ marginRight: 8 }}
              onConfirm={handleSave}
              loading={saveLoading}
            >
              Save
            </SaveConfirmButton>
          }
        </div>


        <div className="workorderdetail-toolbtn-right">
          <div
            className={`workorderdetail-toolbtn-right-btn${workorderDetailData?.source_flag === 'pms' ? ' disabled' : ''}`}
            onClick={() => {
              if (workorderDetailData?.source_flag === 'pms') return;
              setShowAttachmentPanel(v => !v);
            }}
          >
            Attachments
            <div
              className='workorderdetail-toolbtn-right-btn-count'
            >
              {workorderDetailData?.attach_count || '0'}
            </div>
          </div>
          <div
            className={`workorderdetail-toolbtn-right-btn${workorderDetailData?.source_flag === 'pms' ? ' disabled' : ''}`}
            onClick={() => {
              if (workorderDetailData?.source_flag === 'pms') return;
              setShowEmailPanel(v => !v);
            }}
          >
            Emails
            <div
              className='workorderdetail-toolbtn-right-btn-count'
            >
              {workorderDetailData?.email_count || '0'}
            </div>
          </div>

          <div
            className={`workorderdetail-toolbtn-right-btn${workorderDetailData?.source_flag === 'pms' ? ' disabled' : ''}`}
            onClick={() => {
              if (workorderDetailData?.source_flag === 'pms') return;
              setShowAnPanel(v => !v);
            }}
          >
            A/N
          </div>

          <div
            className={`workorderdetail-toolbtn-right-btn${workorderDetailData?.source_flag === 'pms' ? ' disabled' : ''}`}
            style={{
              backgroundColor: showPdfPanel ? '#1f63fb' : '',
              color: showPdfPanel ? '#ffffff' : '',
            }}
            onClick={async () => {
              if (workorderDetailData?.source_flag === 'pms') return;
              // 检查是否有PDF URL
              const pdfUrl = workorderDetailData?.agent_file_url;
              if (!pdfUrl) {
                messageApi.warning('No PDF URL available');
                return;
              }

              // 检测PDF是否可以在iframe中预览
              try {
                // 可以预览，打开右侧面板
                setPdfDisplayUrl(pdfUrl);
                setShowPdfPanel(v => !v);
              } catch (error) {
                console.error('Error checking PDF preview capability:', error);
                // 如果检测失败，尝试预览，如果不行用户可以手动下载
                setPdfDisplayUrl(pdfUrl);
                setShowPdfPanel(v => !v);
              }
            }}
          >
            {/* PDF */}
            <FilePdfFilled style={{ fontSize: 16 }} />
          </div>

          <div
            className={`workorderdetail-toolbtn-right-btn${workorderDetailData?.source_flag === 'pms' ? ' disabled' : ''}`}
            onClick={async () => {
              if (workorderDetailData?.source_flag === 'pms') return;
              setShowVersionSnapshotDrawer(true);
              await fetchVersionSnapshots();
            }}
          >
            <HistoryOutlined style={{ fontSize: 15, marginRight: 6 }} />
            View Log
          </div>

        </div>
      </div>

      <div className="workorderdetail-floating-tracknote">
        <Button
          type="primary"
          size="large"
          icon={<EditFilled />}
          onClick={handleOpenTrackNoteModal}
        >
          Track
        </Button>
      </div>



      {/* 左 60% 表单 + 右 40% PDF面板*/}
      <div className={
        showPdfPanel ? 'workorderdetail-content pdf-split' :
          showEmailPanel ? 'workorderdetail-content.split' :
            'workorderdetail-content'
      }>
        <div className="workorderdetail-left">
          <Form
            form={form}
            layout="horizontal"
            labelAlign="right"
          >


            {/* 顶部关键字段行: HBL / MBL / AMS B\/L / PIC */}
            <div className="detail-top-keyfields">
              {[
                formConfig.entrustmentInfo.find(f => f.name === 'booking_no'),
                formConfig.entrustmentInfo.find(f => f.name === 'ams_bl'),
                formConfig.entrustmentInfo.find(f => f.name === 'hbl_no'),
                formConfig.entrustmentInfo.find(f => f.name === 'mbl_no'),
                formConfig.workorderInfo.find(f => f.name === 'pic'),
              ].map((field, index) => field && (
                <div key={index} className="detail-top-keyfield">
                  <FormField field={field} />
                </div>
              ))}
            </div>

            {/* 两列布局: Shipper + Transport Information */}
            <div className="detail-two-col-layout">
              <FormSection title="Shipper / Exporter" columns={1}>
                <div className="party-info-normal-value">
                  {renderPartyInfoSection(formConfig.shipperInfo)}
                </div>
              </FormSection>
              <FormSection title="Transport Information" columns={1}>
                {renderTransportInfoSection(
                  formConfig.entrustmentInfo.filter(f => !['hbl_no', 'mbl_no', 'ams_bl', 'booking_no'].includes(f.name))
                )}
              </FormSection>
            </div>

            {/* 两列布局: Consignee + Notify Party */}
            <div className="detail-two-col-layout">
              <FormSection title="Consignee" columns={1}>
                <div className="party-info-normal-value">
                  {renderPartyInfoSection(formConfig.consigneeInfo)}
                </div>
              </FormSection>
              <FormSection title="Notify Party" columns={1}>
                <div className="party-info-normal-value">
                  {renderPartyInfoSection(formConfig.notifyPartyInfo)}
                </div>
              </FormSection>
            </div>



            {/* Container Cargo Information */}
            <div className="form-section">
              <div className="section-header">
                <div className="header-line" />
                <h3>Container Cargo Information</h3>
              </div>


              <FormSection title="Container Cargo" columns={4} fields={formConfig.container_cargo} />


              <Table
                key={`container-table-${isEdit ? 'edit' : 'view'}`}
                columns={containerColumns}
                dataSource={containerTableDataSource}
                pagination={false}
                rowKey='key'
                scroll={{ x: 'max-content' }}
                onRow={(record) => {
                  if (record.key === 'total') {
                    return {
                      style: {
                        background: '#f5f5f5',
                        fontWeight: 'bold'
                      }
                    };
                  }
                  return {};
                }}
              />

              {isEdit && (
                <div className="containerinfo-bottom" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="default"
                    onClick={handleAddContainer}
                    icon={<PlusOutlined />}
                    style={{ marginTop: 16 }}
                  />
                </div>
              )}
            </div>


            
            {/* Cost Information  */}
            <FormSection
              title="Cost Information"
              columns={1}
              className="cost-info-section"
              extra={
                <Tooltip title="View original fee snapshots and reset">
                  <Button
                    type="primary"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={handleOpenFeeSnapshotDrawer}
                    style={{
                      backgroundColor: '#fa8c16',
                      borderColor: '#fa8c16',
                      color: '#fff',
                      fontWeight: 600,
                      boxShadow: '0 2px 4px rgba(250,140,22,0.3)',
                      '&:hover': {
                        backgroundColor: '#ffa94dff',
                        borderColor: '#fa8c16',
                        color: '#fff',
                      },
                    }}
                  >
                    Original Manifests
                  </Button>
                </Tooltip>
              }
            >
              {/* 第一部分：COLLECT FOR AGENT */}
              <Row gutter={16} style={{ height: '100%' }}>
                <Col span={17}>
                  <h4>Collect for Agent</h4>
                  <Row gutter={16}>
                    {formConfig.costInfo.collectForAgent.map((field) => (
                      <Col span={6} key={field.name}>
                        <Form.Item
                          label={renderCostFieldLabel(field)}
                          name={field.name}
                          initialValue={0}
                          rules={[{ required: field.required }]}
                        >
                          <InputNumber
                            style={{ width: '100%', fontWeight: 700 }}
                            formatter={formatCostNumberInput}
                            parser={parseCostNumberInput}
                            disabled={!isEdit || (releaseDateValue && userInfo?.userRole !== 'Admin')}
                            step="1"
                            onChange={(value) => handleCurrencyChange(field.name, value)}
                            precision={2}
                            controls={false}
                          />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                </Col>

                {/* 第二部分：PAY TO PRIME */}
                <Divider type="vertical" style={{ height: '100%' }} />

                <Col span={6}>
                  <h4>Pay to Prime</h4>
                  {formConfig.costInfo.payToPrime.map((field) => (
                    <Form.Item label={renderCostFieldLabel(field)} name={field.name} key={field.name} initialValue={0}>
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={formatCostNumberInput}
                        parser={parseCostNumberInput}
                        disabled={!isEdit || (releaseDateValue && userInfo?.userRole !== 'Admin')}
                        step="1"
                        onChange={(value) => handleCurrencyChange(field.name, value)}
                        precision={2}
                        controls={false}
                      />
                    </Form.Item>
                  ))}
                </Col>
              </Row>

              {/* 第三部分：Cost Bottom */}
              <Row gutter={24} style={{ marginTop: 8 }}>

                {/* note */}
                <Col span={16}>
                  <Row gutter={12}>
                    {formConfig.costInfo.note.map((field, index) => (
                      field && field.label ? (
                        <Col span={12} key={index}>
                          <Form.Item
                            label={field.label}
                            name={field.name}
                            key={index}
                          >
                            <Input.TextArea
                              rows={4}
                              disabled={!isEdit || (field.name === 'hold_note' && releaseDateValue && userInfo?.userRole !== 'Admin')}
                            />
                          </Form.Item>
                        </Col>
                      ) : null
                    ))}
                  </Row>
                </Col>


                {/* balance change 和 costbottom */}
                <Col span={8}>
                  <div className='balance-change'>
                    {/* <div className='balance-change-label' >Balance Change</div> */}
                    <div className="balance-change-content">
                      {formConfig.costInfo.balance_change.map((field, index) => {
                        if (index % 2 === 0) {
                          const nextField = formConfig.costInfo.balance_change[index + 1];
                          return (
                            <div className="balance-change-row" key={index}>
                              <div className="balance-change-row-label">{`${field.label}.`}</div>
                              <Row gutter={16}>
                                <Col span={12}
                            >
                                  <Form.Item
                                    // label={field.label}
                                    name={field.name}
                                    initialValue={field.type === 'currency' ? 0 : undefined}
                                    style={{ marginBottom: 0 }}
                                  >
                                    {field.type === 'currency' ? (
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={formatCostNumberInput}
                                        parser={parseCostNumberInput}
                                        step="1"
                                        onChange={(value) => handleCurrencyChange(field.name, value)}
                                        disabled={!isEdit || (releaseDateValue && userInfo?.userRole !== 'Admin')}
                                        precision={2}
                                        controls={false}
                                      />
                                    ) : (
                                      <Input
                                        disabled={!isEdit || (releaseDateValue && userInfo?.userRole !== 'Admin')}
                                      />
                                    )}
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    // label={nextField?.label}
                                    name={nextField?.name}
                                    initialValue={nextField?.type === 'currency' ? 0 : undefined}
                                    style={{ marginBottom: 0 }}
                                  >
                                    {nextField?.type === 'currency' ? (
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={formatCostNumberInput}
                                        parser={parseCostNumberInput}
                                        step="0.01"
                                        onChange={(value) => handleCurrencyChange(nextField.name, value)}
                                        disabled={!isEdit || (releaseDateValue && userInfo?.userRole !== 'Admin')}
                                        precision={2}
                                        controls={false}
                                      />
                                    ) : (
                                      <Input
                                        disabled={!isEdit || (releaseDateValue && userInfo?.userRole !== 'Admin')}
                                      />
                                    )}
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>


                  <Row gutter={24} style={{ margin: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    {formConfig.costInfo.costbottom.map((field, index) => (
                      field && field.label ? (
                        <Col span={24} key={index}>
                          <Form.Item
                            label={renderCostFieldLabel(field)}
                            name={field.name}
                            key={index}
                            initialValue={0}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              formatter={formatCostNumberInput}
                              parser={parseCostNumberInput}
                              step="0.01"
                              disabled={true}
                              precision={2}
                              controls={false}
                            />
                          </Form.Item>
                        </Col>
                      ) : null
                    ))}
                  </Row>
                </Col>
              </Row>
            </FormSection>




            <FormSection title="Release Information" columns={5} fields={formConfig.releaseInfo} className="aligned-field-section" />

            {/* Shipment Update Information */}
            <FormSection title="Shipment Update Information" columns={4} fields={formConfig.shipmentUpdateInfo} className="aligned-field-section" />



            <div className="detail-two-col-layout" style={{ display: 'grid', gridTemplateColumns: '4fr 3fr', gap: '16px', alignItems: 'start' }}>
              <div className="form-section" style={{ minWidth: 0 }}>
                {/* <div className="section-header">
                  <div className="header-line" />
                  <h3>Container Return / Pickup Info</h3>
                </div> */}
                {containerTableRows.length === 0 ? (
                  <Empty description="No container rows" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Table
                    key={`container-bottom-table-${isEdit ? 'edit' : 'view'}`}
                    dataSource={containerTableRows}
                    pagination={false}
                    rowKey="key"
                    size="small"
                    tableLayout="fixed"
                    columns={containerBottomFields.map((field) => {
                      const fieldName = field.dataIndex;
                      const isContainerNo = fieldName === 'container_no';
                      const compactWidthMap = {
                        container_no: 150,
                        return_date: 90,
                        pickup_date: 90,
                        pickup_number: 110,
                        last_free_day: 90,
                        dd_note: 140,
                      };

                      return {
                        title: field.onHeaderCell ? <span style={{ color: '#c21617' }}>{field.title}</span> : field.title,
                        dataIndex: fieldName,
                        key: field.key || fieldName,
                        width: compactWidthMap[fieldName] || field.width,
                        render: (value, record) => {
                          const displayValue = CONTAINER_DATE_FIELDS.has(fieldName)
                            ? formatDisplayDate(value)
                            : value;

                          if (!isEdit || isContainerNo) {
                            return (
                              <Input
                                value={displayValue || ''}
                                disabled
                              />
                            );
                          }

                          return renderContainerBottomInput(record, fieldName);
                        }
                      };
                    })}
                  />
                )}
              </div>

              <div className="form-section aligned-field-section" style={{ minWidth: 0 }}>
                {/* <div className="section-header">
                  <div className="header-line" />
                  <h3>Others</h3>
                </div> */}
                {renderPositionedFields(formConfig.otherInfo)}
              </div>
            </div>

            {/* 字段索引模糊查询自动补全 */}
            <FieldIndexAutocomplete
              form={form}
              fieldNames={['port_of_loading', 'port_of_discharge', 'place_of_delivery', 'freight_location', 'freight_tel', 'it_number', 'consignee_email', 'notify_email']}
              userInfo={userInfo}
              onFieldLoadingChange={handleAutocompleteFieldLoading}
            />
          </Form>
        </div>



        {/* PDF 显示面板 */}
        {(showPdfPanel && pdfDisplayUrl) && (
          <div className="workorderdetail-right-pdf">
            <div className="pdf-panel-header">
              <div className="pdf-panel-header-left">
                <h3>PDF Preview</h3>
                <div>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = pdfDisplayUrl;
                      a.download = 'document.pdf';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      messageApi.success('File download started');
                    }}
                    title="Download PDF"
                  >
                  </Button>
                </div>
              </div>
              <Button
                type="text"
                onClick={() => setShowPdfPanel(false)}
                style={{ fontSize: '16px' }}
              >
                ×
              </Button>
            </div>
            <div className="pdf-panel-content">
              {(pdfDisplayUrl && pdfDisplayUrl.includes('pai-ocr')) ? (
                <iframe
                  src={pdfDisplayUrl.includes('pai-ocr') ? pdfDisplayUrl : null}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title="PDF Preview"
                  onLoad={(e) => {
                    // 检查iframe是否成功加载内容
                    try {
                      const iframe = e.target;
                      // 如果iframe的contentDocument为null，可能是跨域或下载问题
                      if (!iframe.contentDocument && !iframe.contentWindow) {
                        console.warn('PDF may have triggered download instead of preview');
                      }
                    } catch (error) {
                      console.warn('Cannot access iframe content, PDF may have been downloaded');
                    }
                  }}
                  onError={() => {
                    console.error('Failed to load PDF in iframe');
                    messageApi.error('PDF preview failed, please try downloading the file');
                  }}
                />
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#999',
                  gap: '16px'
                }}>
                  <FilePdfOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                  <span>No PDF URL available</span>
                </div>
              )}

            </div>
          </div>
        )}




        {/* 邮件抽屉 */}
        <EmailDrawer
          open={showEmailPanel}
          onClose={() => setShowEmailPanel(false)}
          emailLoading={emailLoading}
          emailError={emailError}
          emails={emails}
          selectedEmail={selectedEmail}
          setSelectedEmail={setSelectedEmail}
        />

        {/* 附件抽屉 */}
        <AttachmentDrawer
          open={showAttachmentPanel}
          onClose={() => {
            setShowAttachmentPanel(false);
            setActiveAttachmentTab('attachments');
            setSelectedAttachment(null);
            setSelectedAnAttachment(null);
            setSelectedArchiveAttachmentKeys([]);
            setAnEmailType(0);
          }}
          hblNo={hblNo}
          activeAttachmentTab={activeAttachmentTab}
          setActiveAttachmentTab={setActiveAttachmentTab}
          setSelectedAttachment={setSelectedAttachment}
          setSelectedAnAttachment={setSelectedAnAttachment}
          selectedArchiveAttachmentKeys={selectedArchiveAttachmentKeys}
          setSelectedArchiveAttachmentKeys={setSelectedArchiveAttachmentKeys}
          setAnEmailType={setAnEmailType}
          attachments={attachments}
          attachmentLoading={attachmentLoading}
          attachmentError={attachmentError}
          selectedAttachment={selectedAttachment}
          getFileIcon={getFileIcon}
          renderAttachmentPreview={renderAttachmentPreview}
          archiveEmailAttachmentsLoading={archiveEmailAttachmentsLoading}
          handleArchiveEmailAttachments={handleArchiveEmailAttachments}
          uploadedFiles={uploadedFiles}
          uploadedFilesLoading={uploadedFilesLoading}
          uploadedFilesError={uploadedFilesError}
          editingRemarkId={editingRemarkId}
          editingRemarkType={editingRemarkType}
          setEditingRemarkId={setEditingRemarkId}
          setEditingRemarkType={setEditingRemarkType}
          savingRemark={savingRemark}
          handleUpdateRemark={handleUpdateRemark}
          handleDeleteUploadedAttachment={handleDeleteUploadedAttachment}
          fetchUploadedFiles={fetchUploadedFiles}
          anAttachments={anAttachments}
          anAttachmentsLoading={anAttachmentsLoading}
          anAttachmentsError={anAttachmentsError}
          selectedAnAttachment={selectedAnAttachment}
          anEmailType={anEmailType}
          fetchAnAttachments={fetchAnAttachments}
          handleDeleteAnAttachment={handleDeleteAnAttachment}
          FilesUploadPanel={FilesUploadPanel}
          AnAttachmentsPanel={AnAttachmentsPanel}
        />


        {/* AN Drawer */}
        <ANDrawer
          open={showAnPanel}
          onClose={() => setShowAnPanel(false)}
          selectedAnTemplate={selectedAnTemplate}
          setSelectedAnTemplate={setSelectedAnTemplate}
          handleGeneratePdf={handleGeneratePdf}
          generateanLoading={generateanLoading}
          sendanLoading={sendanLoading}
          handleSendAnEmail={handleSendAnEmail}
          anEmailRecords={anEmailRecords}
          anRecordsLoading={anRecordsLoading}
          fetchAnEmailRecords={fetchAnEmailRecords}
          pdfUrl={pdfUrl}
        />

        <Drawer
          title="Order Version Snapshots"
          placement="right"
          onClose={() => setShowVersionSnapshotDrawer(false)}
          open={showVersionSnapshotDrawer}
          width={720}
          destroyOnClose={false}
          extra={(
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={versionSnapshotsLoading}
              onClick={fetchVersionSnapshots}
            >
              Refresh
            </Button>
          )}
        >
          {versionSnapshotsLoading ? (
            <div className="workorderdetail-snapshot-loading">
              <Spin tip="Loading snapshots..." size="large" />
            </div>
          ) : versionSnapshots.length ? (
            <div className="workorderdetail-snapshot-list">
              {versionSnapshots.map((snapshot) => (
                <div
                  key={snapshot?.id || `${snapshot?.shippingOrderId}-${snapshot?.version}`}
                  className="workorderdetail-snapshot-card"
                >
                  <div className="workorderdetail-snapshot-card-header">
                    <div className="workorderdetail-snapshot-card-title">
                      Version {snapshot?.version ?? '-'}
                    </div>
                    <Tag color="blue">{snapshot?.hblNo || workorderDetailData?.hbl_no || hblNo}</Tag>
                  </div>

                  <div className="workorderdetail-snapshot-meta">
                    <span>Operator: {snapshot?.operatorName || '-'}</span>
                    <span>Time: {snapshot?.createdAt || '-'}</span>
                  </div>

                  <table className="workorderdetail-snapshot-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Old Value</th>
                        <th>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(snapshot?.changes || []).map((change, index) => (
                        <tr key={`${snapshot?.id || snapshot?.version}-${change?.fieldName || 'field'}-${index}`}>
                          <td>{change?.fieldName || '-'}</td>
                          <td>{formatSnapshotValue(change?.oldValue)}</td>
                          <td>{formatSnapshotValue(change?.newValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          ) : (
            <Empty description="No version snapshots" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Drawer>

        <Modal
          title="Track Note Content"
          open={showTrackNoteModal}
          onCancel={() => setShowTrackNoteModal(false)}
          width={980}
          destroyOnClose={false}
          footer={[
            <Button key="refresh" onClick={fetchTrackNotes} loading={trackNoteLoading}>
              Refresh
            </Button>,
            <Button key="add" onClick={handleAddTrackNote}>
              Add Row
            </Button>,
            <Button key="cancel" onClick={() => setShowTrackNoteModal(false)}>
              Cancel
            </Button>,
            <Button key="save" type="primary" onClick={handleSaveTrackNotes} loading={trackNoteSaving}>
              Save
            </Button>,
          ]}
        >
          {trackNoteLoading ? (
            <div className="workorderdetail-snapshot-loading">
              <Spin tip="Loading track notes..." size="large" />
            </div>
          ) : (
            <Table
              columns={trackNoteColumns}
              dataSource={trackNoteTableDataSource}
              pagination={false}
              rowKey="key"
              scroll={{ x: 'max-content', y: 480 }}
              locale={{ emptyText: 'No track notes' }}
            />
          )}
        </Modal>


        {/* 主数据一级字段变更记录的Modal */}
        <FieldHistoryModal
          title="Field Change History"
          open={isfieldDescShow}
          onCancel={() => {
            setFieldDescriptions([]);
            setIsfieldDescShow(false);
          }}
          data={fieldDescriptions}
        />


        {/* 集装箱字段变更记录的Modal */}
        <FieldHistoryModal
          title="Container Field Change History"
          open={isContainerFieldDescShow}
          onCancel={() => {
            setContainerFieldDescriptions([]);
            setIsContainerFieldDescShow(false);
          }}
          data={containerFieldDescriptions}
        />


        {/* 费用原始快照 Drawer */}
        <Drawer
          title="Original Snapshots"
          placement="right"
          width="65%"
          open={feeSnapshotDrawerOpen}
          onClose={() => setFeeSnapshotDrawerOpen(false)}
          zIndex={1100}
        >
          {feeSnapshotLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" tip="Loading snapshots..." />
            </div>
          ) : feeSnapshots.length === 0 ? (
            <Empty description="No snapshots found" />
          ) : (
            feeSnapshots.map((group) => {
              const feesId = group.feesSnapshot?.id;
              const selectedFields = feesId ? (feeSnapshotSelectedFields[feesId] || []) : [];
              const allChecked = feesId && selectedFields.length === RESETTABLE_FEE_FIELDS.length;
              const someChecked = selectedFields.length > 0 && !allChecked;
              return (
                <div
                  key={group.sourceFlag}
                  style={{
                    marginBottom: 24,
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 16,
                    backgroundColor: '#fafafa',
                  }}
                >
                  {/* 快照头部 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Tag color={group.sourceFlag === 'pai' ? 'blue' : group.sourceFlag === 'pms' ? 'green' : 'orange'}>
                        {group.sourceFlag?.toUpperCase()}
                      </Tag>
                      {group.orderSnapshot && (
                        <span style={{ color: '#666', fontSize: 12 }}>HBL: {group.orderSnapshot.hblNo}</span>
                      )}
                      {!group.orderSnapshot && group.feesSnapshot && (
                        <span style={{ color: '#666', fontSize: 12 }}>HBL: {group.feesSnapshot.hblNo}</span>
                      )}
                      <span style={{ color: '#999', fontSize: 12 }}>|</span>
                      <span style={{ color: '#666', fontSize: 12 }}>
                        Created: {group.orderSnapshot?.createdTime || group.feesSnapshot?.createdTime || '-'}
                      </span>
                    </div>
                    {feesId && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Checkbox
                          checked={allChecked}
                          indeterminate={someChecked}
                          onChange={(e) => handleSelectAllFeeSnapshotFields(feesId, e.target.checked)}
                        >
                          Select All
                        </Checkbox>
                        <Button
                          type="primary"
                          size="small"
                          danger
                          loading={feeSnapshotResetting}
                          disabled={selectedFields.length === 0}
                          onClick={() => handleResetFeeSnapshot(feesId)}
                        >
                          Reset Selected ({selectedFields.length})
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* 订单主表快照（只读） */}
                  {group.orderSnapshot && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>Order Snapshot</div>
                      <Row gutter={[12, 4]}>
                        {ORDER_SNAPSHOT_FIELDS.map(field => (
                          <Col span={4} key={field.key}>
                            <div style={{ fontSize: 11, color: '#999' }}>{field.label}</div>
                            <div style={{ fontSize: 12, color: '#333', wordBreak: 'break-word' }}>
                              {group.orderSnapshot[field.key] != null ? String(group.orderSnapshot[field.key]) : <span style={{ color: '#ccc' }}>-</span>}
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}

                  {/* 费用快照 - 可重置字段（可选中） */}
                  {group.feesSnapshot && (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
                        Fee Snapshot {feesId ? '' : <span style={{ color: '#999', fontWeight: 400 }}>(No resettable data)</span>}
                      </div>
                      <Row gutter={[12, 8]}>
                        {RESETTABLE_FEE_FIELDS.map(field => {
                          const value = group.feesSnapshot[field.key];
                          const isChecked = selectedFields.includes(field.key);
                          return (
                            <Col span={4} key={field.key}>
                              <div
                                style={{
                                  padding: '8px 10px',
                                  borderRadius: 6,
                                  border: isChecked ? '2px solid #1f63fb' : '1px solid #e8e8e8',
                                  backgroundColor: isChecked ? '#e6f4ff' : '#fff',
                                  cursor: feesId ? 'pointer' : 'not-allowed',
                                  transition: 'all 0.2s',
                                  opacity: feesId ? 1 : 0.6,
                                }}
                                onClick={() => feesId && handleToggleFeeSnapshotField(feesId, field.key)}
                              >
                                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{field.label}</div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: value != null ? '#000' : '#ccc' }}>
                                  {value != null ? `$${Number(value).toFixed(2)}` : '-'}
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>

                      {/* 费用快照 - 不可重置字段（只读） */}
                      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed #e8e8e8' }}>
                        <Row gutter={[12, 4]}>
                          {FEE_READONLY_FIELDS.map(field => (
                            <Col span={4} key={field.key}>
                              <div style={{ fontSize: 11, color: '#999' }}>{field.label}</div>
                              <div style={{ fontSize: 12, color: '#333', wordBreak: 'break-word' }}>
                                {group.feesSnapshot[field.key] || <span style={{ color: '#ccc' }}>-</span>}
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </>
                  )}

                  {/* 集装箱快照（只读） */}
                  {group.containerSnapshots && group.containerSnapshots.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed #e8e8e8' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
                        Container Snapshots ({group.containerSnapshots.length})
                      </div>
                      {group.containerSnapshots.map((container) => (
                        <div
                          key={container.id || container.containerNo}
                          style={{
                            marginBottom: 8,
                            padding: '8px 12px',
                            border: '1px solid #e8e8e8',
                            borderRadius: 6,
                            backgroundColor: '#fff',
                          }}
                        >
                          <Row gutter={[12, 4]}>
                            {CONTAINER_SNAPSHOT_FIELDS.map(field => (
                              <Col span={4} key={field.key}>
                                <div style={{ fontSize: 11, color: '#999' }}>{field.label}</div>
                                <div style={{ fontSize: 12, color: '#333', wordBreak: 'break-word' }}>
                                  {container[field.key] != null ? String(container[field.key]) : <span style={{ color: '#ccc' }}>-</span>}
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Drawer>


      </div>
    </div>
  );
};

export default WorkOrderDetail;
